import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useGameEngine from '../hooks/useGameEngine';
import { createFiniteMode } from '../gameModes/finiteMode';
import ProgressCircles from '../ProgressCircles';
import { getLevelById, ARCADE_LEVELS } from './arcadeData';

/* ─── helpers ─── */
const getMaeColor = (mae, target) => {
  if (mae === null) return '#888';
  if (mae <= target) return '#4caf50';
  if (mae <= target * 1.5) return '#ff9800';
  return '#f44336';
};

/* ══════════════════════════════════════════════════
   DOCTOR THEME — medical office decorations
══════════════════════════════════════════════════ */
const OfficePoster = () => (
  <svg viewBox="0 0 90 120" width="90" height="120" style={{ display: 'block' }}>
    <rect width="90" height="120" fill="#f5f0e0" stroke="#8b7355" strokeWidth="3" rx="2" />
    <rect x="3" y="3" width="84" height="114" fill="none" stroke="#d4b896" strokeWidth="1" />
    <ellipse cx="45" cy="22" rx="14" ry="16" fill="#e8c8a0" stroke="#a0785a" strokeWidth="1.5" />
    <path d="M33 36 Q28 55 26 75 L36 76 L38 95 L52 95 L54 76 L64 75 Q62 55 57 36 Z"
      fill="#e8c8a0" stroke="#a0785a" strokeWidth="1.5" />
    <line x1="26" y1="43" x2="18" y2="58" stroke="#a0785a" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="64" y1="43" x2="72" y2="58" stroke="#a0785a" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="38" y1="95" x2="36" y2="114" stroke="#a0785a" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="52" y1="95" x2="54" y2="114" stroke="#a0785a" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="62" y1="30" x2="75" y2="25" stroke="#c0a060" strokeWidth="1" />
    <text x="76" y="26" fontSize="5" fill="#806040" fontFamily="serif">Head</text>
    <line x1="62" y1="50" x2="75" y2="48" stroke="#c0a060" strokeWidth="1" />
    <text x="76" y="49" fontSize="5" fill="#806040" fontFamily="serif">Torso</text>
    <text x="15" y="115" fontSize="6" fill="#806040" fontFamily="serif" fontStyle="italic">Anatomy I</text>
  </svg>
);

const WallClock = () => (
  <svg viewBox="0 0 80 80" width="80" height="80" style={{ display: 'block' }}>
    <circle cx="40" cy="40" r="38" fill="#f8f4e8" stroke="#8b7355" strokeWidth="3" />
    <circle cx="40" cy="40" r="35" fill="none" stroke="#c8b090" strokeWidth="1" />
    {[...Array(12)].map((_, i) => {
      const a = (i * 30 - 90) * Math.PI / 180;
      const r1 = i % 3 === 0 ? 28 : 30;
      return (
        <line key={i}
          x1={40 + r1 * Math.cos(a)} y1={40 + r1 * Math.sin(a)}
          x2={40 + 34 * Math.cos(a)} y2={40 + 34 * Math.sin(a)}
          stroke="#6b5040" strokeWidth={i % 3 === 0 ? 2.5 : 1.5} />
      );
    })}
    <line x1="40" y1="40" x2="27" y2="24" stroke="#3a2a1a" strokeWidth="3" strokeLinecap="round" />
    <line x1="40" y1="40" x2="50" y2="16" stroke="#3a2a1a" strokeWidth="2" strokeLinecap="round" />
    <circle cx="40" cy="40" r="3" fill="#8b5e3c" />
  </svg>
);

const OfficeWindow = ({ style }) => (
  <div style={{
    width: '140px', height: '190px',
    background: 'linear-gradient(180deg, #b8d4f0 0%, #d0e8ff 50%, #c4daf4 100%)',
    border: '8px solid #a08060', borderRadius: '4px 4px 0 0',
    position: 'relative', overflow: 'hidden',
    boxShadow: 'inset 0 0 20px rgba(0,80,160,0.15), 2px 2px 8px rgba(0,0,0,0.3)',
    ...style,
  }}>
    <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '4px', background: '#a08060', transform: 'translateX(-50%)' }} />
    <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '4px', background: '#a08060', transform: 'translateY(-50%)' }} />
    <div style={{ position: 'absolute', top: '15%', left: '8%', width: '50px', height: '18px', background: 'rgba(255,255,255,0.7)', borderRadius: '12px' }} />
    <div style={{ position: 'absolute', top: '12%', left: '18%', width: '32px', height: '14px', background: 'rgba(255,255,255,0.8)', borderRadius: '10px' }} />
    <div style={{ position: 'absolute', top: '30%', right: '10%', width: '40px', height: '14px', background: 'rgba(255,255,255,0.6)', borderRadius: '10px' }} />
    <div style={{ position: 'absolute', top: 0, left: '30%', right: 0, bottom: 0, background: 'linear-gradient(120deg, rgba(255,255,200,0.18) 0%, transparent 60%)' }} />
    {[...Array(6)].map((_, i) => (
      <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${i * 17}%`, height: '3px', background: 'rgba(180,150,100,0.4)' }} />
    ))}
  </div>
);

const Diploma = () => (
  <div style={{
    width: '120px', background: '#fdf6e3', border: '3px solid #c8a84b',
    padding: '8px', textAlign: 'center', boxShadow: '2px 2px 6px rgba(0,0,0,0.2)',
    fontFamily: 'Georgia, serif',
  }}>
    <div style={{ fontSize: '7px', color: '#8b6914', letterSpacing: '0.1em', fontStyle: 'italic', borderBottom: '1px solid #c8a84b', paddingBottom: '4px', marginBottom: '4px' }}>UNIVERSITY OF MEDICINE</div>
    <div style={{ fontSize: '6px', color: '#5a3e1b', marginBottom: '4px', lineHeight: 1.5 }}>
      This certifies that<br /><em style={{ fontSize: '7px', color: '#8b4513' }}>Dr. Tempus</em><br />has completed studies in
    </div>
    <div style={{ fontSize: '6.5px', color: '#8b4513', fontWeight: 'bold', marginBottom: '4px' }}>CHRONOLOGICAL MEDICINE</div>
    <div style={{ fontSize: '5px', color: '#a08060', marginTop: '4px' }}>⚕ Class of 1987 ⚕</div>
    <div style={{ marginTop: '6px', borderTop: '1px solid #c8a84b', paddingTop: '4px' }}>
      <div style={{ width: '60px', borderBottom: '1px solid #8b6914', margin: '0 auto', height: '14px' }} />
      <div style={{ fontSize: '5px', color: '#8b6914', marginTop: '2px' }}>Dean's Signature</div>
    </div>
  </div>
);

const DoctorBackground = () => (
  <>
    <div style={{
      position: 'fixed', inset: 0, zIndex: 0,
      background: 'linear-gradient(180deg, #e8e0cc 0%, #ddd4be 65%, #c4a87a 65%, #b09060 100%)',
    }} />
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '35%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(0,0,0,0.06) 59px, rgba(0,0,0,0.06) 60px), repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(0,0,0,0.06) 59px, rgba(0,0,0,0.06) 60px), #c4a87a',
    }} />
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '340px', height: '100vh', zIndex: 2,
      background: 'linear-gradient(115deg, rgba(255,250,200,0.16) 0%, transparent 55%)',
      pointerEvents: 'none',
    }} />
  </>
);

const DoctorLeftDecor = () => (
  <div style={{ position: 'fixed', left: '20px', top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 3, gap: '20px' }}>
    <OfficeWindow style={{ marginBottom: '0' }} />
    <Diploma />
  </div>
);

const DoctorRightDecor = () => (
  <div style={{ position: 'fixed', right: '20px', top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 3, gap: '16px' }}>
    <WallClock />
    <OfficePoster />
    <div style={{ width: '80px', height: '90px', background: 'linear-gradient(180deg, #e8e0d0 0%, #d4c8b0 100%)', border: '2px solid #9a8060', borderRadius: '2px', position: 'relative', boxShadow: '2px 3px 8px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%', borderRight: '1px solid #9a8060' }}>
        <div style={{ position: 'absolute', top: '30%', right: '12px', width: '6px', height: '6px', borderRadius: '50%', background: '#c0a040', border: '1px solid #8b7020' }} />
        <div style={{ position: 'absolute', top: '55%', right: '12px', width: '6px', height: '6px', borderRadius: '50%', background: '#c0a040', border: '1px solid #8b7020' }} />
      </div>
    </div>
  </div>
);

const DoctorBottomBar = () => (
  <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '55px', background: 'linear-gradient(180deg, #8b6a3a 0%, #6b4e28 100%)', borderTop: '3px solid #4a3018', zIndex: 3, display: 'flex', alignItems: 'center', padding: '0 24px', gap: '20px', boxShadow: '0 -4px 12px rgba(0,0,0,0.3)' }}>
    <div style={{ position: 'relative', width: '40px', height: '50px', animation: 'lampSway 6s ease-in-out infinite', transformOrigin: 'bottom center' }}>
      <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '36px', height: '18px', background: 'linear-gradient(180deg, #c8a040 0%, #a07820 100%)', borderRadius: '18px 18px 2px 2px', border: '1.5px solid #6b5018' }} />
      <div style={{ position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', width: '3px', height: '26px', background: '#8b6020', borderRadius: '2px' }} />
      <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '20px', height: '6px', background: '#5a3a10', borderRadius: '3px' }} />
    </div>
    <div style={{ display: 'flex', gap: '16px', fontSize: '1.5rem' }}>
      <span>🩺</span><span>💊</span>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   RIDER THEME — street / pizzeria scene
══════════════════════════════════════════════════ */

/* City building silhouettes against sunset sky */
const CityBuildings = () => (
  <svg width="100%" height="100%" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ display: 'block' }}>
    {/* Back layer – shorter buildings */}
    <rect x="0"    y="180" width="88"  height="140" fill="#1a0d30" />
    <rect x="95"   y="140" width="72"  height="180" fill="#1a0d30" />
    <rect x="175"  y="160" width="105" height="160" fill="#1a0d30" />
    <rect x="288"  y="110" width="65"  height="210" fill="#1a0d30" />
    <rect x="362"  y="150" width="90"  height="170" fill="#1e1035" />
    <rect x="462"  y="120" width="78"  height="200" fill="#1e1035" />
    <rect x="550"  y="100" width="115" height="220" fill="#1e1035" />
    <rect x="675"  y="85"  width="82"  height="235" fill="#160c2a" />
    <rect x="768"  y="120" width="95"  height="200" fill="#160c2a" />
    <rect x="873"  y="100" width="72"  height="220" fill="#160c2a" />
    <rect x="955"  y="130" width="105" height="190" fill="#160c2a" />
    <rect x="1070" y="95"  width="84"  height="225" fill="#1a0d30" />
    <rect x="1164" y="75"  width="92"  height="245" fill="#1a0d30" />
    <rect x="1266" y="110" width="114" height="210" fill="#1a0d30" />
    <rect x="1390" y="130" width="90"  height="190" fill="#1a0d30" />

    {/* Lit windows – yellow warm glow */}
    {[
      [12,190],[12,207],[30,195],[52,188],[52,205],[72,200],
      [108,150],[108,167],[128,155],[150,148],[150,165],
      [192,170],[192,187],[210,175],[232,163],[232,180],
      [300,120],[300,137],[318,115],[340,128],[340,145],
      [374,160],[394,155],[394,172],[414,162],
      [475,130],[475,147],[495,135],[515,128],[515,145],
      [565,110],[565,127],[583,115],[605,110],[605,127],[625,118],
      [688,95],[688,112],[706,100],[726,92],[726,109],[748,103],
      [780,130],[800,125],[800,142],[820,132],
      [885,110],[905,105],[905,122],[925,110],
      [968,140],[988,135],[988,152],[1008,140],
      [1083,105],[1103,100],[1103,117],[1123,105],[1145,112],
      [1177,85],[1197,80],[1197,97],[1217,88],[1238,82],[1238,99],
      [1280,120],[1300,115],[1300,132],[1320,122],[1342,115],
    ].map(([x, y], i) => (
      <rect key={i} x={x} y={y} width="10" height="7" rx="1"
        fill="#f5d060" opacity={0.5 + (i % 5) * 0.1} />
    ))}

    {/* Water towers */}
    <rect x="670" y="60" width="52" height="26" fill="#130a22" />
    <rect x="664" y="84" width="64" height="5"  fill="#130a22" />
    <line x1="696" y1="88" x2="696" y2="100" stroke="#130a22" strokeWidth="5" />
    <rect x="1162" y="50" width="48" height="24" fill="#130a22" />
    <rect x="1157" y="72" width="58" height="4"  fill="#130a22" />
    <line x1="1186" y1="76" x2="1186" y2="88" stroke="#130a22" strokeWidth="4" />
  </svg>
);

const RiderBackground = () => (
  <>
    {/* Sunset sky */}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 0,
      background: 'linear-gradient(180deg, #120820 0%, #2d1050 15%, #6a1e6a 32%, #b83050 48%, #e86040 62%, #f59045 74%, #f5c866 84%, #d4a040 100%)',
    }} />
    {/* City silhouette layer */}
    <div style={{ position: 'fixed', bottom: '18%', left: 0, right: 0, height: '55%', zIndex: 1, pointerEvents: 'none' }}>
      <CityBuildings />
    </div>
    {/* Sidewalk */}
    <div style={{
      position: 'fixed', bottom: '5%', left: 0, right: 0, height: '14%', zIndex: 2,
      background: 'repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(0,0,0,0.08) 79px, rgba(0,0,0,0.08) 80px), repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(0,0,0,0.06) 39px, rgba(0,0,0,0.06) 40px), #909098',
      borderTop: '3px solid #6a6a78',
    }} />
    {/* Road */}
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '5%', zIndex: 2,
      background: '#2a2a38',
      borderTop: '2px solid #3a3a48',
    }}>
      {/* Road markings */}
      <div style={{ position: 'absolute', top: '40%', left: 0, right: 0, height: '2px', background: 'repeating-linear-gradient(90deg, #f5c800 0px, #f5c800 40px, transparent 40px, transparent 80px)' }} />
    </div>
    {/* Ambient glow from sunset on ground */}
    <div style={{ position: 'fixed', bottom: '5%', left: 0, right: 0, height: '6px', zIndex: 3, background: 'linear-gradient(180deg, rgba(245,144,69,0.4) 0%, transparent 100%)', pointerEvents: 'none' }} />
  </>
);

const BRICK = 'repeating-linear-gradient(0deg, transparent 0px, transparent 11px, rgba(0,0,0,0.2) 11px, rgba(0,0,0,0.2) 13px), repeating-linear-gradient(90deg, transparent 0px, transparent 28px, rgba(0,0,0,0.12) 28px, rgba(0,0,0,0.12) 30px), #7a2e20';

/* Left side: Pizzeria facade */
const PizzeriaFront = () => (
  <div style={{
    position: 'fixed', left: 0, top: '8%', bottom: '19%', width: '170px',
    zIndex: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: 'inset -8px 0 24px rgba(0,0,0,0.4)',
  }}>

    {/* ① Upper brick wall — signs only */}
    <div style={{ flex: 1, position: 'relative', background: BRICK }}>
      {/* Neon PIZZERIA sign */}
      <div style={{
        position: 'absolute', top: '14px', left: '50%', transform: 'translateX(-50%)',
        color: '#f5c800', fontSize: '11px', fontFamily: "'Press Start 2P', monospace",
        fontWeight: 'bold', whiteSpace: 'nowrap', letterSpacing: '1px',
        textShadow: '0 0 6px #f5c800, 0 0 18px #f5c800, 0 0 36px rgba(245,200,0,0.6)',
        border: '2px solid #f5c800', padding: '4px 8px',
        boxShadow: '0 0 10px rgba(245,200,0,0.6), inset 0 0 6px rgba(245,200,0,0.15)',
        background: 'rgba(20,10,0,0.7)',
        animation: 'neonFlicker 4s ease-in-out infinite',
      }}>
        🍕 PIZZERIA
      </div>
      {/* Green neon sub-sign */}
      <div style={{
        position: 'absolute', top: '62px', left: '50%', transform: 'translateX(-50%)',
        color: '#44ff88', fontSize: '8px', fontFamily: 'monospace',
        whiteSpace: 'nowrap', letterSpacing: '2px',
        textShadow: '0 0 5px #44ff88, 0 0 15px #44ff88',
      }}>
        OPEN 24H
      </div>
    </div>

    {/* ② Awning — correct position above the window */}
    <div style={{
      height: '32px', flexShrink: 0, position: 'relative',
      background: 'repeating-linear-gradient(90deg, #cc2200 0px, #cc2200 18px, #ffffff 18px, #ffffff 36px)',
      borderTop: '3px solid #8b1c00', borderBottom: '2px solid #8b1c00',
      boxShadow: '0 6px 10px rgba(0,0,0,0.45)',
    }}>
      {/* Fringe triangles */}
      {[...Array(9)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute', bottom: '-10px', left: `${i * 19}px`,
          width: 0, height: 0,
          borderLeft: '9px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: `11px solid ${i % 2 === 0 ? '#cc2200' : '#ffffff'}`,
        }} />
      ))}
    </div>

    {/* ③ Lower brick section — window + door + chalkboard */}
    <div style={{ height: '160px', flexShrink: 0, position: 'relative', background: BRICK }}>
      {/* Shop window with warm amber glow */}
      <div style={{
        position: 'absolute', top: '14px', left: '12px', right: '60px', bottom: '10px',
        background: 'linear-gradient(180deg, rgba(255,160,40,0.15) 0%, rgba(255,130,20,0.38) 100%)',
        border: '5px solid #3a2210',
        boxShadow: 'inset 0 0 24px rgba(255,140,30,0.6), 0 0 18px rgba(255,120,20,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        fontSize: '1.3rem',
      }}>
        🍕 🫙
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '3px', background: '#3a2210' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '3px', background: '#3a2210' }} />
      </div>

      {/* Door */}
      <div style={{
        position: 'absolute', bottom: 0, right: '12px',
        width: '44px', height: '110px',
        background: '#2a1408', border: '4px solid #3a2210', borderBottom: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 5px',
      }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f5c800', boxShadow: '0 0 4px #f5c800' }} />
      </div>

      {/* Chalkboard menu */}
      <div style={{
        position: 'absolute', bottom: '8px', left: '8px',
        width: '38px', height: '52px',
        background: '#1a2a1a', border: '3px solid #3a2a10', padding: '3px',
      }}>
        <div style={{ fontSize: '5px', color: '#c8e8b8', fontFamily: 'monospace', lineHeight: 1.6 }}>
          PIZZA<br />MENU<br />──<br />MARG<br />€8
        </div>
      </div>
    </div>

  </div>
);

/* Right side: street lamp + scooter */
const StreetRight = () => (
  <div style={{
    position: 'fixed', right: '16px', top: '8%', bottom: '19%',
    width: '140px', zIndex: 3,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'space-between',
  }}>
    {/* Street lamp */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
      {/* Lamp head */}
      <div style={{
        width: '60px', height: '18px',
        background: 'linear-gradient(180deg, #d4c080 0%, #a09040 100%)',
        border: '2px solid #706020', borderRadius: '6px 6px 2px 2px',
        position: 'relative',
        boxShadow: '0 0 20px rgba(255,220,80,0.6), 0 0 40px rgba(255,200,40,0.3)',
      }}>
        {/* Lamp glow halo */}
        <div style={{
          position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
          width: '120px', height: '120px',
          background: 'radial-gradient(ellipse, rgba(255,220,80,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        {/* Bulb glow */}
        <div style={{ position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)', width: '14px', height: '10px', background: '#fff8c0', borderRadius: '50%', boxShadow: '0 0 8px #ffe060' }} />
        {/* Lamp arm curve */}
        <div style={{ position: 'absolute', bottom: '-22px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '22px', background: '#888060', borderRadius: '2px' }} />
      </div>
      {/* Pole */}
      <div style={{ width: '7px', flex: 1, background: 'linear-gradient(90deg, #777060 0%, #aaa888 40%, #666050 100%)', borderRadius: '3px', marginTop: '22px' }} />
      {/* Base */}
      <div style={{ width: '28px', height: '10px', background: '#555040', borderRadius: '3px', marginTop: '0' }} />
    </div>

    {/* Parked scooter */}
    <svg viewBox="0 0 110 80" width="110" height="80" style={{ flexShrink: 0, marginBottom: '4px' }}>
      <defs>
        <radialGradient id="scWheelGrad" cx="50%" cy="50%" r="50%">
          <stop offset="70%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#333" />
        </radialGradient>
        <linearGradient id="scBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f5c800" />
          <stop offset="100%" stopColor="#c49800" />
        </linearGradient>
      </defs>
      {/* Rear wheel */}
      <circle cx="20" cy="60" r="17" fill="url(#scWheelGrad)" stroke="#333" strokeWidth="2" />
      <circle cx="20" cy="60" r="12" fill="none" stroke="#555" strokeWidth="3" />
      <circle cx="20" cy="60" r="4" fill="#666" />
      <line x1="20" y1="48" x2="20" y2="72" stroke="#555" strokeWidth="1.5" />
      <line x1="8" y1="60" x2="32" y2="60" stroke="#555" strokeWidth="1.5" />
      {/* Front wheel */}
      <circle cx="88" cy="62" r="15" fill="url(#scWheelGrad)" stroke="#333" strokeWidth="2" />
      <circle cx="88" cy="62" r="10" fill="none" stroke="#555" strokeWidth="3" />
      <circle cx="88" cy="62" r="4" fill="#666" />
      {/* Body/frame */}
      <path d="M22 55 Q35 40 55 36 Q72 32 82 38 Q88 42 88 48 L88 50" stroke="url(#scBodyGrad)" strokeWidth="8" fill="none" strokeLinecap="round" />
      {/* Under-body */}
      <path d="M22 58 Q40 58 60 54 Q74 52 88 52" stroke="#a08000" strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* Seat */}
      <ellipse cx="50" cy="33" rx="20" ry="6" fill="#2a2a2a" stroke="#111" strokeWidth="1.5" />
      {/* Handlebar */}
      <rect x="79" y="28" width="4" height="22" rx="2" fill="#666" />
      <rect x="72" y="27" width="18" height="5" rx="2" fill="#444" />
      {/* Headlight */}
      <ellipse cx="98" cy="48" rx="7" ry="5" fill="#f5e060" stroke="#c0a020" strokeWidth="1.5" />
      {/* Delivery bag on back */}
      <rect x="8" y="26" width="28" height="28" rx="3" fill="#f5c800" stroke="#a08000" strokeWidth="1.5" />
      <rect x="12" y="30" width="20" height="16" rx="2" fill="#1e7a00" stroke="#136000" strokeWidth="1" />
      <text x="22" y="41" textAnchor="middle" fontSize="5" fill="#f5d700" fontWeight="bold" fontFamily="Arial">RUSH</text>
      {/* Kickstand */}
      <line x1="45" y1="56" x2="38" y2="68" stroke="#666" strokeWidth="3" strokeLinecap="round" />
    </svg>
  </div>
);

const RiderBottomBar = () => (
  <div style={{
    position: 'fixed', bottom: '5%', left: 0, right: 0, height: '14%',
    zIndex: 2, display: 'flex', alignItems: 'flex-end', padding: '0 20px 6px',
    pointerEvents: 'none', gap: '20px',
  }}>
    {/* Litter / sidewalk items */}
    <span style={{ fontSize: '1.2rem', opacity: 0.7 }}>📦</span>
    <span style={{ fontSize: '1rem', opacity: 0.6, marginLeft: '30px' }}>🚧</span>
  </div>
);

/* ══════════════════════════════════════════════════
   BAKER THEME — warm artisan bakery interior
══════════════════════════════════════════════════ */

const BakeryBackground = () => (
  <>
    {/* Warm cream walls */}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 0,
      background: 'linear-gradient(180deg, #f5e8c0 0%, #edd9a0 55%, #c8854a 55%, #b87038 100%)',
    }} />
    {/* Tile floor pattern */}
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '45%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(0,0,0,0.07) 49px, rgba(0,0,0,0.07) 50px), repeating-linear-gradient(0deg, transparent, transparent 49px, rgba(0,0,0,0.07) 49px, rgba(0,0,0,0.07) 50px), #c8854a',
    }} />
    {/* Warm ambient light from above */}
    <div style={{
      position: 'fixed', top: 0, left: '30%', right: '30%', height: '180px', zIndex: 2,
      background: 'radial-gradient(ellipse, rgba(255,230,140,0.5) 0%, transparent 70%)',
      pointerEvents: 'none',
    }} />
    {/* Subtle wall warmth vignette */}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2,
      background: 'radial-gradient(ellipse at 50% 40%, transparent 40%, rgba(140,80,20,0.18) 100%)',
      pointerEvents: 'none',
    }} />
  </>
);

/* Left: Brick oven + ingredients */
const BakeryOven = () => (
  <div style={{
    position: 'fixed', left: 0, top: '8%', bottom: '55%', width: '180px',
    zIndex: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: 'inset -8px 0 24px rgba(0,0,0,0.3)',
  }}>
    {/* Oven SVG */}
    <svg viewBox="0 0 180 220" width="180" height="220" style={{ display: 'block', flexShrink: 0 }}>
      <defs>
        <radialGradient id="ovenGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ff9030" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#c04800" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="brickRow" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#8b3a20" />
          <stop offset="100%" stopColor="#6a2a14" />
        </linearGradient>
      </defs>
      {/* Oven arch/surround — brick */}
      <rect x="10" y="10" width="160" height="200" rx="6" fill="#7a3018" stroke="#5a2010" strokeWidth="2" />
      {/* Brick rows */}
      {[...Array(10)].map((_, row) => (
        <g key={row}>
          {[...Array(row % 2 === 0 ? 4 : 3)].map((_, col) => {
            const bw = row % 2 === 0 ? 36 : 46;
            const bx = row % 2 === 0 ? 14 + col * 38 : -4 + col * 48;
            return (
              <rect key={col}
                x={bx} y={14 + row * 18}
                width={bw} height={15}
                rx="1" fill={row % 3 === 0 ? '#8a3820' : '#7a2e18'}
                stroke="#5a2010" strokeWidth="1"
              />
            );
          })}
        </g>
      ))}
      {/* Arch opening */}
      <path d="M32 100 Q32 60 90 58 Q148 60 148 100 L148 185 L32 185 Z"
        fill="#1a0a04" stroke="#5a2010" strokeWidth="2" />
      {/* Fire glow inside */}
      <ellipse cx="90" cy="170" rx="44" ry="18" fill="#ff6020" opacity="0.8" />
      <ellipse cx="90" cy="165" rx="32" ry="14" fill="#ffa030" opacity="0.7" />
      <ellipse cx="90" cy="158" rx="22" ry="10" fill="#ffcc50" opacity="0.6" />
      {/* Flame tongues */}
      <path d="M70 165 Q74 148 78 155 Q80 142 85 150 Q88 138 90 148 Q93 136 96 148 Q100 140 103 152 Q107 144 110 160" fill="none" stroke="#ff8820" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
      {/* Glow halo from opening */}
      <ellipse cx="90" cy="120" rx="58" ry="62" fill="url(#ovenGlow)" />
      {/* Arch keystone */}
      <path d="M82 58 Q90 52 98 58" fill="#a04828" stroke="#5a2010" strokeWidth="1.5" />
    </svg>
  </div>
);

const BakeryIngredients = () => (
  <div style={{
    position: 'fixed', left: '14px', bottom: '55%', top: 'auto',
    zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px',
  }}>
    {/* Flour sack */}
    <svg viewBox="0 0 90 100" width="90" height="100" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="flourSack" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f5f0e8" />
          <stop offset="100%" stopColor="#d8ceb8" />
        </linearGradient>
      </defs>
      {/* Sack body */}
      <path d="M18 28 Q14 50 12 72 Q12 88 45 90 Q78 88 78 72 Q76 50 72 28 Z"
        fill="url(#flourSack)" stroke="#b8a888" strokeWidth="2" />
      {/* Tied top */}
      <path d="M30 28 Q45 18 60 28 Q55 10 45 8 Q35 10 30 28 Z" fill="#ddd4c0" stroke="#b8a888" strokeWidth="1.5" />
      <path d="M38 24 Q45 14 52 24" fill="none" stroke="#a09070" strokeWidth="2" strokeLinecap="round" />
      {/* FLOUR text */}
      <text x="45" y="58" textAnchor="middle" fontSize="9" fill="#8a7858" fontFamily="Georgia, serif" fontStyle="italic" fontWeight="bold">FLOUR</text>
      <text x="45" y="70" textAnchor="middle" fontSize="7" fill="#a09070" fontFamily="Georgia, serif">5 kg</text>
      {/* Fold crease */}
      <path d="M20 45 Q45 42 70 45" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="1.5" />
      <path d="M16 62 Q45 59 74 62" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="1" />
      {/* Flour dust spill */}
      <ellipse cx="45" cy="90" rx="36" ry="6" fill="rgba(255,255,255,0.6)" />
    </svg>
    {/* Mixing bowl */}
    <svg viewBox="0 0 80 55" width="80" height="55" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="bowlGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e8e0d0" />
          <stop offset="100%" stopColor="#b8a888" />
        </linearGradient>
      </defs>
      <path d="M6 12 Q6 46 40 48 Q74 46 74 12 Z" fill="url(#bowlGrad)" stroke="#a09070" strokeWidth="2" />
      <path d="M6 12 Q40 20 74 12" fill="none" stroke="#ccc0a8" strokeWidth="2" />
      {/* Dough inside */}
      <path d="M14 18 Q40 28 66 18 Q66 38 40 40 Q14 38 14 18 Z" fill="#f5e8c0" stroke="#d4c090" strokeWidth="1.5" />
      {/* Bowl foot */}
      <ellipse cx="40" cy="48" rx="18" ry="5" fill="#b0a080" stroke="#a09070" strokeWidth="1" />
    </svg>
  </div>
);

const BakeryLeftDecor = () => (
  <div className="decor-hide" style={{ position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 3, flexDirection: 'column' }}>
    <BakeryOven />
    <BakeryIngredients />
  </div>
);

/* Right: Wooden display shelf with baked goods */
const BakeryShelf = () => (
  <div style={{
    position: 'fixed', right: '14px', top: '8%', bottom: '18%',
    width: '155px', zIndex: 3,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0',
  }}>
    <svg viewBox="0 0 155 400" width="155" height="400" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="shelfWood" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a06030" />
          <stop offset="100%" stopColor="#7a4418" />
        </linearGradient>
        <linearGradient id="breadGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#d4900a" />
          <stop offset="100%" stopColor="#a06008" />
        </linearGradient>
        <linearGradient id="cakeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8f0e0" />
          <stop offset="100%" stopColor="#e8d8c0" />
        </linearGradient>
      </defs>

      {/* Back wall strip */}
      <rect x="0" y="0" width="155" height="400" fill="#e8d8a0" opacity="0.4" />

      {/* === TOP SHELF === */}
      <rect x="5" y="88" width="145" height="14" rx="3" fill="url(#shelfWood)" stroke="#5a3010" strokeWidth="1.5" />
      {/* Shelf shadow */}
      <rect x="5" y="100" width="145" height="4" rx="1" fill="rgba(0,0,0,0.2)" />
      {/* Shelf bracket left */}
      <path d="M16 88 L16 110 L30 110" fill="none" stroke="#6a3818" strokeWidth="3" strokeLinecap="round" />
      {/* Shelf bracket right */}
      <path d="M139 88 L139 110 L125 110" fill="none" stroke="#6a3818" strokeWidth="3" strokeLinecap="round" />

      {/* Items on top shelf — small pies + jar */}
      {/* Mini pie 1 */}
      <ellipse cx="32" cy="76" rx="20" ry="8" fill="#c87810" stroke="#905808" strokeWidth="1.5" />
      <ellipse cx="32" cy="73" rx="16" ry="6" fill="#e8a828" />
      <ellipse cx="32" cy="71" rx="10" ry="4" fill="#c84030" />
      <ellipse cx="32" cy="88" rx="22" ry="6" fill="#d4a060" stroke="#a07030" strokeWidth="1" />
      {/* Mini pie 2 */}
      <ellipse cx="80" cy="76" rx="20" ry="8" fill="#c87810" stroke="#905808" strokeWidth="1.5" />
      <ellipse cx="80" cy="73" rx="16" ry="6" fill="#e8a828" />
      <ellipse cx="80" cy="71" rx="10" ry="4" fill="#a03828" />
      <ellipse cx="80" cy="88" rx="22" ry="6" fill="#d4a060" stroke="#a07030" strokeWidth="1" />
      {/* Jam jar */}
      <rect x="110" y="62" width="28" height="30" rx="3" fill="#f8e8d8" stroke="#c8a080" strokeWidth="1.5" />
      <rect x="106" y="58" width="36" height="10" rx="3" fill="#e8d0b0" stroke="#c8a080" strokeWidth="1.5" />
      <rect x="112" y="64" width="24" height="14" rx="2" fill="#e04828" opacity="0.8" />
      <text x="124" y="74" textAnchor="middle" fontSize="6" fill="#fff" fontFamily="serif">JAM</text>
      <ellipse cx="124" cy="92" rx="16" ry="4" fill="#d4a870" />

      {/* === MIDDLE SHELF === */}
      <rect x="5" y="200" width="145" height="14" rx="3" fill="url(#shelfWood)" stroke="#5a3010" strokeWidth="1.5" />
      <rect x="5" y="212" width="145" height="4" rx="1" fill="rgba(0,0,0,0.2)" />
      <path d="M16 200 L16 222 L30 222" fill="none" stroke="#6a3818" strokeWidth="3" strokeLinecap="round" />
      <path d="M139 200 L139 222 L125 222" fill="none" stroke="#6a3818" strokeWidth="3" strokeLinecap="round" />

      {/* Items on middle shelf — bread loaves */}
      {/* Sourdough */}
      <path d="M18 174 Q22 156 45 156 Q68 156 72 174 Q72 194 45 196 Q18 194 18 174 Z"
        fill="url(#breadGrad)" stroke="#8a5010" strokeWidth="1.5" />
      <path d="M26 162 Q45 155 64 162" fill="none" stroke="#c07820" strokeWidth="2" strokeLinecap="round" />
      {/* Score marks */}
      <path d="M30 168 Q45 163 60 168" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M28 178 Q45 172 62 178" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.2" strokeLinecap="round" />
      {/* Bread highlight */}
      <ellipse cx="38" cy="162" rx="14" ry="7" fill="rgba(255,200,80,0.3)" transform="rotate(-15,38,162)" />
      {/* Baguette */}
      <path d="M88 190 Q90 162 100 158 Q110 155 115 160 Q120 155 126 158 Q136 162 138 190 Z"
        fill="#d4900a" stroke="#8a5010" strokeWidth="1.5" />
      <path d="M92 186 Q113 164 134 186" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.2" />
      <ellipse cx="113" cy="166" rx="12" ry="6" fill="rgba(255,200,80,0.25)" transform="rotate(-5,113,166)" />

      {/* === BOTTOM SHELF — cake display === */}
      <rect x="5" y="320" width="145" height="14" rx="3" fill="url(#shelfWood)" stroke="#5a3010" strokeWidth="1.5" />
      <rect x="5" y="334" width="145" height="4" rx="1" fill="rgba(0,0,0,0.2)" />
      <path d="M16 320 L16 342 L30 342" fill="none" stroke="#6a3818" strokeWidth="3" strokeLinecap="round" />
      <path d="M139 320 L139 342 L125 342" fill="none" stroke="#6a3818" strokeWidth="3" strokeLinecap="round" />

      {/* Layered cake */}
      {/* Base */}
      <ellipse cx="65" cy="318" rx="40" ry="10" fill="#d4a050" stroke="#a07020" strokeWidth="1.5" />
      {/* Layer 1 */}
      <rect x="27" y="294" width="76" height="26" rx="3" fill="url(#cakeGrad)" stroke="#c8a880" strokeWidth="1.5" />
      {/* Frosting layer 1 */}
      <path d="M27 294 Q65 288 103 294" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" opacity="0.9" />
      {/* Layer 2 */}
      <rect x="34" y="272" width="62" height="24" rx="3" fill="url(#cakeGrad)" stroke="#c8a880" strokeWidth="1.5" />
      {/* Frosting layer 2 */}
      <path d="M34 272 Q65 266 96 272" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" opacity="0.9" />
      {/* Top layer */}
      <rect x="41" y="253" width="48" height="21" rx="3" fill="url(#cakeGrad)" stroke="#c8a880" strokeWidth="1.5" />
      <path d="M41 253 Q65 247 89 253" fill="none" stroke="white" strokeWidth="5" strokeLinecap="round" opacity="0.9" />
      {/* Cake decorations */}
      <circle cx="55" cy="251" r="4" fill="#e84040" stroke="#c02020" strokeWidth="1" />
      <circle cx="65" cy="249" r="4" fill="#e84040" stroke="#c02020" strokeWidth="1" />
      <circle cx="75" cy="251" r="4" fill="#e84040" stroke="#c02020" strokeWidth="1" />
      {/* Candle */}
      <rect x="63" y="238" width="4" height="13" rx="1" fill="#f8e040" stroke="#d0b820" strokeWidth="1" />
      <path d="M65 236 Q63 230 65 226 Q67 230 65 236 Z" fill="#ff9020" />
      <circle cx="65" cy="227" r="2" fill="#ffee80" opacity="0.8" />
    </svg>
  </div>
);

/* Rolling pin + utensils hanging on wall */
const BakeryWallItems = () => (
  <div style={{
    position: 'fixed', right: '170px', top: '10%',
    zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px',
  }}>
    {/* Wall clock */}
    <svg viewBox="0 0 72 72" width="72" height="72" style={{ display: 'block' }}>
      <circle cx="36" cy="36" r="34" fill="#f8f0d8" stroke="#9a6830" strokeWidth="3" />
      <circle cx="36" cy="36" r="31" fill="none" stroke="#c8a860" strokeWidth="1" />
      {[...Array(12)].map((_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        const r1 = i % 3 === 0 ? 25 : 27;
        return (
          <line key={i}
            x1={36 + r1 * Math.cos(a)} y1={36 + r1 * Math.sin(a)}
            x2={36 + 30 * Math.cos(a)} y2={36 + 30 * Math.sin(a)}
            stroke="#8a5828" strokeWidth={i % 3 === 0 ? 2.5 : 1.5} />
        );
      })}
      <line x1="36" y1="36" x2="25" y2="21" stroke="#4a2810" strokeWidth="3" strokeLinecap="round" />
      <line x1="36" y1="36" x2="46" y2="14" stroke="#4a2810" strokeWidth="2" strokeLinecap="round" />
      <circle cx="36" cy="36" r="3" fill="#9a6830" />
    </svg>
    {/* Hanging rolling pin */}
    <svg viewBox="0 0 24 80" width="24" height="80" style={{ display: 'block' }}>
      {/* Hook string */}
      <line x1="12" y1="0" x2="12" y2="8" stroke="#8a6040" strokeWidth="1.5" />
      {/* Handle top */}
      <rect x="8" y="8" width="8" height="12" rx="4" fill="#b07830" stroke="#7a5018" strokeWidth="1.5" />
      {/* Main roller */}
      <rect x="4" y="20" width="16" height="40" rx="8" fill="#c88c3a" stroke="#8a5818" strokeWidth="2" />
      {/* Roller highlight */}
      <path d="M7 25 Q12 22 17 25" fill="none" stroke="rgba(255,220,120,0.5)" strokeWidth="2" strokeLinecap="round" />
      {/* Handle bottom */}
      <rect x="8" y="60" width="8" height="12" rx="4" fill="#b07830" stroke="#7a5018" strokeWidth="1.5" />
    </svg>
  </div>
);

const BakeryBottomBar = () => (
  <div style={{
    position: 'fixed', bottom: 0, left: 0, right: 0, height: '52px',
    background: 'linear-gradient(180deg, #9a6030 0%, #7a4418 100%)',
    borderTop: '3px solid #5a2e0a', zIndex: 3,
    display: 'flex', alignItems: 'center', padding: '0 24px', gap: '18px',
    boxShadow: '0 -4px 14px rgba(0,0,0,0.35)',
  }}>
    {/* Counter edge highlight */}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'rgba(255,200,100,0.3)' }} />
    {/* Flour puffs on counter */}
    <div style={{ width: '28px', height: '10px', background: 'rgba(255,255,255,0.55)', borderRadius: '50%', filter: 'blur(2px)' }} />
    <div style={{ width: '18px', height: '8px', background: 'rgba(255,255,255,0.45)', borderRadius: '50%', filter: 'blur(2px)', marginLeft: '20px' }} />
    <div style={{ display: 'flex', gap: '14px', fontSize: '1.4rem', marginLeft: 'auto' }}>
      <span>🥚</span><span>🧈</span>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   TRAIN DRIVER THEME — Japanese Shinkansen station
══════════════════════════════════════════════════ */

/* N700 Shinkansen bullet train SVG — nose points left (train moves left) */
const ShinkansenCar = () => (
  <svg viewBox="0 0 760 78" width="760" height="78" style={{ display: 'block' }}>
    <defs>
      <linearGradient id="scBody" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#f8f8f8" />
        <stop offset="55%" stopColor="#f0f0ee" />
        <stop offset="100%" stopColor="#dcdcda" />
      </linearGradient>
      <linearGradient id="scNose" x1="100%" y1="0%" x2="0%" y2="0%">
        <stop offset="0%" stopColor="#f8f8f8" />
        <stop offset="100%" stopColor="#e8e8e6" />
      </linearGradient>
    </defs>

    {/* ── BODY (cars 1-16 implied) ── */}
    <rect x="80" y="10" width="640" height="58" fill="url(#scBody)" />

    {/* ── AERODYNAMIC NOSE — pointing left ── */}
    <path d="M 80 10 Q 40 10 14 32 Q 8 39 8 39 Q 8 39 14 46 Q 40 68 80 68 L 80 10 Z"
      fill="url(#scNose)" stroke="#d8d8d4" strokeWidth="1" />

    {/* ── TAIL END (blunt right) ── */}
    <path d="M 720 10 Q 742 10 750 39 Q 742 68 720 68 L 720 10 Z"
      fill="#f0f0ee" stroke="#d8d8d4" strokeWidth="1" />

    {/* ── BLUE LOWER STRIPE ── */}
    <rect x="80" y="54" width="640" height="14" fill="#1e4a8a" />
    <path d="M 80 54 Q 30 54 10 58 L 10 60 Q 30 68 80 68 Z" fill="#1e4a8a" />
    <path d="M 720 54 Q 745 54 750 60 L 750 62 Q 746 68 720 68 Z" fill="#1e4a8a" />

    {/* ── BLUE NOSE GRADIENT ── */}
    <path d="M 48 22 Q 14 34 8 39 Q 14 44 48 56 L 48 22 Z" fill="#1e4a8a" opacity="0.55" />

    {/* ── RED ROOF STRIPE ── */}
    <rect x="80" y="10" width="640" height="5" fill="#cc1e1e" />
    <path d="M 80 10 Q 42 10 16 30 Q 35 14 80 15 Z" fill="#cc1e1e" />
    <rect x="720" y="10" width="30" height="5" fill="#cc1e1e" />

    {/* ── WINDSHIELD ── */}
    <path d="M 72 16 Q 32 16 12 34 Q 10 39 12 44 Q 32 62 72 62 L 72 16 Z"
      fill="#9ad4f5" stroke="#78bcdf" strokeWidth="1.2" opacity="0.88" />
    {/* Windshield reflection glare */}
    <path d="M 30 20 Q 18 30 16 39" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="3" strokeLinecap="round" />

    {/* ── WINDOWS — two rows along body ── */}
    {[...Array(13)].map((_, i) => (
      <g key={i}>
        <rect x={90 + i * 48} y="15" width="32" height="20" rx="3"
          fill="#9ad4f5" stroke="#78bcdf" strokeWidth="1" opacity="0.88" />
        {/* Window glare */}
        <path d={`M ${93 + i * 48} 16 Q ${100 + i * 48} 14 ${108 + i * 48} 16`}
          fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" strokeLinecap="round" />
      </g>
    ))}

    {/* ── TRAIN NUMBER ── */}
    <text x="106" y="50" fontSize="9" fill="#1e4a8a" fontFamily="Arial, sans-serif" fontWeight="bold" opacity="0.75">N700</text>
    <text x="108" y="60" fontSize="6" fill="#3a6ab0" fontFamily="Arial, sans-serif" opacity="0.6">のぞみ</text>

    {/* ── PANTOGRAPHS (current collectors on roof) ── */}
    <path d="M 200 10 L 196 2 M 196 2 L 220 2 M 220 2 L 216 10" fill="none" stroke="#888" strokeWidth="1.5" />
    <line x1="196" y1="2" x2="220" y2="2" stroke="#666" strokeWidth="2.5" />
    <path d="M 500 10 L 496 2 M 496 2 L 520 2 M 520 2 L 516 10" fill="none" stroke="#888" strokeWidth="1.5" />
    <line x1="496" y1="2" x2="520" y2="2" stroke="#666" strokeWidth="2.5" />

    {/* ── BOGIE / WHEEL ASSEMBLIES ── */}
    {[60, 220, 390, 560, 680].map((x, i) => (
      <g key={i}>
        <rect x={x} y="68" width="52" height="6" rx="2" fill="#555" />
        <circle cx={x + 10} cy="74" r="4.5" fill="#333" stroke="#1a1a1a" strokeWidth="1.5" />
        <circle cx={x + 42} cy="74" r="4.5" fill="#333" stroke="#1a1a1a" strokeWidth="1.5" />
      </g>
    ))}

    {/* ── BODY OUTLINE ── */}
    <path d="M 80 10 Q 40 10 14 32 Q 8 39 8 39 Q 8 39 14 46 Q 40 68 80 68 L 720 68 Q 742 68 750 54 Q 754 46 752 39 Q 750 32 720 10 Z"
      fill="none" stroke="#c8c8c4" strokeWidth="1.5" />
  </svg>
);

const TrainBackground = () => (
  <>
    {/* Sky */}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 0,
      background: 'linear-gradient(180deg, #3a88d8 0%, #72b8f0 28%, #b4d8f8 52%, #c8c8c0 52%, #b8b4ac 100%)',
    }} />
    {/* Clouds */}
    <div style={{ position: 'fixed', top: '6%', left: '8%', zIndex: 1, pointerEvents: 'none' }}>
      <div style={{ width: '90px', height: '28px', background: 'rgba(255,255,255,0.88)', borderRadius: '50px', filter: 'blur(2px)' }} />
    </div>
    <div style={{ position: 'fixed', top: '10%', left: '18%', zIndex: 1, pointerEvents: 'none' }}>
      <div style={{ width: '60px', height: '20px', background: 'rgba(255,255,255,0.75)', borderRadius: '50px', filter: 'blur(2px)' }} />
    </div>
    <div style={{ position: 'fixed', top: '8%', right: '12%', zIndex: 1, pointerEvents: 'none' }}>
      <div style={{ width: '110px', height: '32px', background: 'rgba(255,255,255,0.85)', borderRadius: '50px', filter: 'blur(2px)' }} />
    </div>
    <div style={{ position: 'fixed', top: '14%', right: '25%', zIndex: 1, pointerEvents: 'none' }}>
      <div style={{ width: '70px', height: '22px', background: 'rgba(255,255,255,0.7)', borderRadius: '50px', filter: 'blur(2px)' }} />
    </div>

    {/* Track bed (gravel) — visible in mid-ground */}
    <div style={{
      position: 'fixed', top: '40%', left: 0, right: 0, height: '12%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, #7a7672 0px, #7a7672 4px, #6a6660 4px, #6a6660 20px)',
    }} />
    {/* Rail ties (sleepers) */}
    <div style={{
      position: 'fixed', top: '40%', left: 0, right: 0, height: '12%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, transparent, transparent 54px, rgba(80,60,40,0.45) 54px, rgba(80,60,40,0.45) 60px)',
      pointerEvents: 'none',
    }} />
    {/* Rails (two steel lines) */}
    <div style={{
      position: 'fixed', top: '44%', left: 0, right: 0, height: '3px', zIndex: 2,
      background: '#b0b0a8', boxShadow: '0 10px 0 #b0b0a8',
    }} />
    {/* Catenary wire */}
    <div style={{
      position: 'fixed', top: '37%', left: 0, right: 0, height: '2px', zIndex: 2,
      background: 'rgba(180,180,160,0.55)',
    }} />

    {/* ── ANIMATED SHINKANSEN ── */}
    <div style={{
      position: 'fixed', top: '36%', left: 0, right: 0, height: '82px',
      zIndex: 2, pointerEvents: 'none',
    }}>
      <div style={{ animation: 'shinkansenRush 11s linear infinite', position: 'absolute', top: 0 }}>
        <ShinkansenCar />
      </div>
    </div>

    {/* Platform surface */}
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '42%', zIndex: 3,
      background: 'repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(0,0,0,0.05) 39px, rgba(0,0,0,0.05) 40px), repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(0,0,0,0.04) 19px, rgba(0,0,0,0.04) 20px), #d4d0c8',
    }} />
    {/* Platform edge line */}
    <div style={{
      position: 'fixed', bottom: '42%', left: 0, right: 0, height: '5px', zIndex: 4,
      background: '#8a8880',
    }} />
    {/* Yellow tactile safety strip */}
    <div style={{
      position: 'fixed', bottom: 'calc(42% + 5px)', left: 0, right: 0, height: '14px', zIndex: 4,
      background: 'repeating-linear-gradient(90deg, #f5c800 0px, #f5c800 22px, #111 22px, #111 28px)',
    }} />
    {/* Station canopy shadow */}
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: '9%', zIndex: 4,
      background: 'linear-gradient(180deg, #182030 0%, rgba(24,32,48,0.7) 60%, transparent 100%)',
      pointerEvents: 'none',
    }} />
  </>
);

/* Departure board (split-flap style) */
const DepartureBoard = () => (
  <div style={{
    background: '#080808', border: '2px solid #2a2a2a', borderRadius: '3px',
    padding: '7px 9px', boxShadow: '0 0 12px rgba(0,0,0,0.7), inset 0 0 4px rgba(0,0,0,0.5)',
    minWidth: '136px',
  }}>
    <div style={{
      color: '#f5c800', fontSize: '7px', fontFamily: 'monospace', letterSpacing: '0.08em',
      borderBottom: '1px solid #2a2a2a', paddingBottom: '4px', marginBottom: '5px',
    }}>
      ▶ 発車時刻 DEPARTURES
    </div>
    {[
      { dest: '東京 TOKYO', num: '12', time: '12:00', live: true },
      { dest: '新大阪 OSAKA', num: '14', time: '12:16', live: false },
      { dest: '博多 HAKATA', num: '16', time: '12:30', live: false },
    ].map(({ dest, num, time, live }) => (
      <div key={num} style={{
        display: 'flex', alignItems: 'center', gap: '5px',
        fontSize: '7px', fontFamily: 'monospace',
        color: live ? '#80ff88' : '#909090',
        marginBottom: '3px', letterSpacing: '0.04em',
        background: live ? 'rgba(0,180,40,0.08)' : 'transparent',
        padding: '1px 2px', borderRadius: '2px',
      }}>
        <span style={{ color: '#f5c800', minWidth: '14px' }}>{num}</span>
        <span style={{ flex: 1 }}>{dest}</span>
        <span style={{ color: live ? '#aaffb0' : '#555' }}>{time}</span>
      </div>
    ))}
    <div style={{ borderTop: '1px solid #1a1a1a', marginTop: '4px', paddingTop: '3px' }}>
      <div style={{ fontSize: '6px', fontFamily: 'monospace', color: '#3a7aff', letterSpacing: '0.05em' }}>
        のぞみ N700 系
      </div>
    </div>
  </div>
);

/* Left decor: platform pillar + JR signs + departure board */
const StationLeftDecor = () => (
  <div className="decor-hide" style={{
    position: 'fixed', left: 0, top: '8%', bottom: '16%',
    width: '182px', zIndex: 5, display: 'flex', alignItems: 'flex-start',
    padding: '0 14px', gap: '10px', flexDirection: 'column',
  }}>
    {/* Platform pillar */}
    <div style={{
      width: '54px', flexShrink: 0, alignSelf: 'flex-end',
      height: '100%', position: 'relative',
      background: 'linear-gradient(90deg, #c0bcb4 0%, #d8d4cc 35%, #e4e0d8 55%, #ccc8c0 100%)',
      border: '2px solid #a8a49c', borderRadius: '4px',
      boxShadow: '3px 0 12px rgba(0,0,0,0.2)',
    }}>
      {/* JR logo plate */}
      <div style={{
        position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
        background: '#1e4a8a', padding: '5px 7px', textAlign: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)', width: '44px', boxSizing: 'border-box',
      }}>
        <div style={{ color: 'white', fontSize: '11px', fontWeight: 'bold', fontFamily: 'Arial', letterSpacing: '0.05em' }}>JR</div>
        <div style={{ color: '#c0d8f0', fontSize: '5.5px', fontFamily: 'Arial' }}>東日本</div>
      </div>
      {/* Platform number sign */}
      <div style={{
        position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)',
        background: '#1e4a8a', textAlign: 'center', padding: '4px 6px', width: '42px', boxSizing: 'border-box',
        border: '1px solid #4a7ab0',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'serif', color: '#f2cc42', lineHeight: 1 }}>1</div>
        <div style={{ fontSize: '6px', fontFamily: 'Arial', color: '#c0d8f0' }}>番線</div>
      </div>
      {/* Pillar horizontal bands */}
      {[20, 40, 60].map(pct => (
        <div key={pct} style={{
          position: 'absolute', top: `${pct}%`, left: 0, right: 0, height: '2px',
          background: 'rgba(0,0,0,0.1)',
        }} />
      ))}
    </div>
    {/* Departure board column */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
      <DepartureBoard />
      {/* Platform direction sign */}
      <div style={{
        background: '#1e4a8a', color: 'white', padding: '5px 8px',
        fontSize: '7px', fontFamily: 'Arial', fontWeight: 'bold',
        border: '1px solid #4a7ab0', boxShadow: '0 2px 5px rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column', gap: '2px',
      }}>
        <div style={{ color: '#f2cc42', fontSize: '6px', letterSpacing: '0.08em' }}>← 東京  TOKYO</div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '2px', color: '#c0d8f0', fontSize: '6px' }}>大阪 OSAKA →</div>
      </div>
    </div>
  </div>
);

/* Vending machine SVG */
const VendingMachine = () => (
  <svg viewBox="0 0 58 105" width="58" height="105" style={{ display: 'block' }}>
    <rect x="2" y="2" width="54" height="101" rx="4" fill="#1e4a8a" stroke="#0e2858" strokeWidth="2" />
    {/* Screen/header */}
    <rect x="8" y="8" width="42" height="26" rx="2" fill="#081428" stroke="#2a5a9a" strokeWidth="1" />
    <text x="29" y="19" textAnchor="middle" fontSize="9" fill="#f2cc42" fontFamily="Arial, sans-serif" fontWeight="bold">JR</text>
    <text x="29" y="28" textAnchor="middle" fontSize="5.5" fill="#82b4f0" fontFamily="Arial, sans-serif">自動販売機</text>
    {/* Product slots */}
    {[0, 1, 2].map(row => [0, 1, 2].map(col => (
      <rect key={`${row}-${col}`} x={9 + col * 14} y={40 + row * 15} width="12" height="13" rx="2"
        fill="#0a1e40" stroke="#2a4a80" strokeWidth="1" />
    )))}
    {/* Coin slot */}
    <rect x="17" y="87" width="24" height="5" rx="1" fill="#0a1828" stroke="#3a5a88" strokeWidth="1" />
    {/* Buy button */}
    <rect x="10" y="94" width="38" height="6" rx="2" fill="#cc1e1e" stroke="#aa0e0e" strokeWidth="1" />
    <text x="29" y="99" textAnchor="middle" fontSize="5" fill="white" fontFamily="Arial">購入</text>
    {/* Highlight stripe */}
    <path d="M 5 5 Q 22 3 54 5" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

/* Station wall clock */
const StationClock = () => (
  <svg viewBox="0 0 80 80" width="80" height="80" style={{ display: 'block' }}>
    <defs>
      <radialGradient id="clkFace" cx="40%" cy="35%" r="60%">
        <stop offset="0%" stopColor="#f8f4f0" />
        <stop offset="100%" stopColor="#e8e4e0" />
      </radialGradient>
    </defs>
    <circle cx="40" cy="40" r="38" fill="#1e4a8a" stroke="#0e2858" strokeWidth="2" />
    <circle cx="40" cy="40" r="34" fill="url(#clkFace)" stroke="#c8d8f0" strokeWidth="1.5" />
    <circle cx="40" cy="40" r="31" fill="none" stroke="#d4e4f8" strokeWidth="0.8" />
    {[...Array(12)].map((_, i) => {
      const a = (i * 30 - 90) * Math.PI / 180;
      const r1 = i % 3 === 0 ? 24 : 27;
      return <line key={i}
        x1={40 + r1 * Math.cos(a)} y1={40 + r1 * Math.sin(a)}
        x2={40 + 30 * Math.cos(a)} y2={40 + 30 * Math.sin(a)}
        stroke="#1e3a6a" strokeWidth={i % 3 === 0 ? 3 : 1.5} strokeLinecap="round" />;
    })}
    {/* Hour hand (12) */}
    <line x1="40" y1="40" x2="40" y2="16" stroke="#1a2840" strokeWidth="3.5" strokeLinecap="round" />
    {/* Minute hand (12) */}
    <line x1="40" y1="40" x2="40" y2="13" stroke="#1a2840" strokeWidth="2.5" strokeLinecap="round" />
    {/* Second hand (3) */}
    <line x1="40" y1="40" x2="67" y2="40" stroke="#dd1e1e" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="40" cy="40" r="3.5" fill="#1e4a8a" />
    <circle cx="40" cy="40" r="1.8" fill="#f2cc42" />
    {/* JR logo under clock center */}
    <text x="40" y="58" textAnchor="middle" fontSize="6" fill="#1e4a8a" fontFamily="Arial" fontWeight="bold">JR</text>
  </svg>
);

/* Right decor: pillar + clock + vending machine + direction signs */
const StationRightDecor = () => (
  <div className="decor-hide" style={{
    position: 'fixed', right: 0, top: '8%', bottom: '16%',
    width: '175px', zIndex: 5,
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
    padding: '0 14px', gap: '10px',
  }}>
    <StationClock />
    {/* Info sign */}
    <div style={{
      background: '#1e4a8a', color: 'white', padding: '6px 10px',
      fontSize: '7px', fontFamily: 'Arial',
      border: '1px solid #4a7ab0', boxShadow: '0 2px 5px rgba(0,0,0,0.4)',
      textAlign: 'center', width: '100%', boxSizing: 'border-box',
    }}>
      <div style={{ color: '#f2cc42', fontSize: '8px', fontWeight: 'bold', marginBottom: '3px' }}>新幹線</div>
      <div style={{ color: '#c0d8f0', letterSpacing: '0.08em' }}>SHINKANSEN</div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: '3px', paddingTop: '3px', color: '#80c4ff', fontSize: '6px' }}>
        東海道・山陽線
      </div>
    </div>
    <VendingMachine />
    {/* Platform pillar (right side) */}
    <div style={{
      width: '50px', height: '120px', alignSelf: 'flex-end', marginTop: 'auto',
      background: 'linear-gradient(90deg, #c0bcb4 0%, #d8d4cc 35%, #e4e0d8 55%, #ccc8c0 100%)',
      border: '2px solid #a8a49c', borderRadius: '4px',
      boxShadow: '-3px 0 12px rgba(0,0,0,0.2)',
    }} />
  </div>
);

/* Bottom bar: platform edge with yellow tactile bumps */
const StationBottomBar = () => (
  <div style={{
    position: 'fixed', bottom: 0, left: 0, right: 0, height: '58px',
    background: 'linear-gradient(180deg, #b8b4ac 0%, #a8a49c 100%)',
    borderTop: '3px solid #888480', zIndex: 5,
    display: 'flex', alignItems: 'center', padding: '0 20px', gap: '14px',
    boxShadow: '0 -6px 16px rgba(0,0,0,0.35)',
  }}>
    {/* Yellow tactile guidance bumps */}
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: '10px',
      background: 'repeating-linear-gradient(90deg, #f5c800 0px, #f5c800 22px, #111 22px, #111 28px)',
    }} />
    {/* Platform items */}
    <div style={{ marginTop: '6px', display: 'flex', gap: '12px', fontSize: '1.3rem' }}>
      <span>🎫</span><span>⏱</span>
    </div>
    {/* Platform number label */}
    <div style={{
      marginTop: '6px', marginLeft: 'auto',
      background: '#1e4a8a', color: '#f2cc42',
      padding: '3px 10px', fontSize: '9px', fontWeight: 'bold',
      fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em',
      border: '1px solid #4a7ab0',
    }}>
      1番線
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   BONZO THEME — concert stage
══════════════════════════════════════════════════ */

/* PA speaker cabinet SVG */
const PASpeaker = ({ x = 0, y = 0, width = 72, height = 100 }) => (
  <svg viewBox="0 0 72 100" width={width} height={height} style={{ display: 'block' }}>
    <rect x="2" y="2" width="68" height="96" rx="4" fill="#1a1a1a" stroke="#0a0a0a" strokeWidth="2" />
    {/* Top HF horn */}
    <rect x="10" y="8" width="52" height="22" rx="3" fill="#111" stroke="#2a2a2a" strokeWidth="1.5" />
    <ellipse cx="36" cy="19" rx="18" ry="8" fill="#0a0a0a" stroke="#383838" strokeWidth="1.5" />
    <ellipse cx="36" cy="19" rx="10" ry="4.5" fill="#050505" />
    <ellipse cx="33" cy="17" rx="4" ry="2" fill="rgba(255,255,255,0.06)" />
    {/* LF woofer */}
    <circle cx="36" cy="66" r="28" fill="#111" stroke="#2a2a2a" strokeWidth="2" />
    <circle cx="36" cy="66" r="22" fill="#0d0d0d" stroke="#333" strokeWidth="1.5" />
    <circle cx="36" cy="66" r="15" fill="#080808" />
    <circle cx="36" cy="66" r="8" fill="#050505" />
    <circle cx="36" cy="66" r="4" fill="#2a2a2a" />
    {/* Speaker grill texture */}
    {[0, 1, 2].map(row => [0, 1, 2, 3].map(col => (
      <circle key={`${row}-${col}`}
        cx={11 + col * 17} cy={36 + row * 8}
        r="1.5" fill="rgba(255,255,255,0.04)" />
    )))}
    {/* Brand strip */}
    <rect x="10" y="92" width="52" height="6" rx="1" fill="#cc2200" />
    <text x="36" y="97" textAnchor="middle" fontSize="5" fill="white" fontFamily="Arial" fontWeight="bold">JBL</text>
    {/* Handle */}
    <rect x="28" y="4" width="16" height="4" rx="2" fill="#333" stroke="#1a1a1a" strokeWidth="1" />
  </svg>
);

/* Stage spotlight/PAR can */
const StageLight = ({ color = '#ff4400', angle = 0 }) => (
  <svg viewBox="0 0 40 60" width="40" height="60" style={{ display: 'block', transform: `rotate(${angle}deg)` }}>
    <rect x="14" y="0" width="12" height="8" rx="2" fill="#333" />
    <path d="M 8 8 L 32 8 L 36 46 L 4 46 Z" fill="#2a2a2a" stroke="#1a1a1a" strokeWidth="1.5" />
    <ellipse cx="20" cy="44" rx="16" ry="6" fill={color} opacity="0.9" />
    <ellipse cx="20" cy="44" rx="10" ry="4" fill="white" opacity="0.5" />
    <ellipse cx="18" cy="42" rx="5" ry="2.5" fill="rgba(255,255,255,0.6)" transform="rotate(-15,18,42)" />
    <rect x="12" y="22" width="16" height="14" rx="2" fill="#1a1a1a" stroke="#333" strokeWidth="1" />
    <circle cx="20" cy="29" r="5" fill={color} opacity="0.6" />
  </svg>
);

/* Marshall 4x12 cabinet */
const MarshallCab = ({ width = 90, height = 100 }) => (
  <svg viewBox="0 0 90 100" width={width} height={height} style={{ display: 'block' }}>
    <rect x="2" y="2" width="86" height="96" rx="3" fill="#1a1208" stroke="#0a0a06" strokeWidth="2" />
    {/* Gold front panel */}
    <rect x="8" y="8" width="74" height="84" rx="2" fill="#141208" stroke="#3a3020" strokeWidth="1.5" />
    {/* Grill cloth */}
    <rect x="10" y="10" width="70" height="66" rx="1"
      fill="none" stroke="rgba(80,70,50,0.4)"
      strokeWidth="0.5"
      style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(60,55,40,0.3) 4px, rgba(60,55,40,0.3) 5px)' }} />
    {/* 4 speaker circles */}
    {[[23, 28], [67, 28], [23, 60], [67, 60]].map(([cx, cy], i) => (
      <g key={i}>
        <circle cx={cx} cy={cy} r="16" fill="#0d0b08" stroke="#2a2518" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="12" fill="#080604" />
        <circle cx={cx} cy={cy} r="6" fill="#050503" />
        <circle cx={cx} cy={cy} r="3" fill="#1a1810" />
        <circle cx={cx - 3} cy={cy - 3} r="2.5" fill="rgba(255,255,255,0.05)" />
      </g>
    ))}
    {/* Marshall logo */}
    <rect x="14" y="80" width="62" height="14" rx="2" fill="#1a1510" stroke="#3a3020" strokeWidth="1" />
    <text x="45" y="91" textAnchor="middle" fontSize="9" fill="#d4a820"
      fontFamily="Georgia, serif" fontStyle="italic" fontWeight="bold">Marshall</text>
    {/* Corner metal caps */}
    {[[2, 2], [82, 2], [2, 90], [82, 90]].map(([x, y], i) => (
      <rect key={i} x={x} y={y} width="8" height="8" rx="1" fill="#3a3a3a" stroke="#1a1a1a" strokeWidth="1" />
    ))}
  </svg>
);

const ConcertBackground = () => (
  <>
    {/* Deep black stage void */}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 0,
      background: 'radial-gradient(ellipse at 50% 20%, #200808 0%, #0e0404 45%, #020101 100%)',
    }} />

    {/* Red spotlight — left */}
    <div style={{
      position: 'fixed', top: 0, left: '5%', width: '32%', height: '100%', zIndex: 1,
      background: 'radial-gradient(ellipse at 40% 0%, rgba(200,20,10,0.28) 0%, transparent 65%)',
      pointerEvents: 'none',
    }} />
    {/* Amber spotlight — center */}
    <div style={{
      position: 'fixed', top: 0, left: '34%', width: '32%', height: '100%', zIndex: 1,
      background: 'radial-gradient(ellipse at 50% 0%, rgba(240,100,10,0.22) 0%, transparent 60%)',
      pointerEvents: 'none',
      animation: 'concertPulse 2.2s ease-in-out infinite',
    }} />
    {/* Blue/purple spotlight — right */}
    <div style={{
      position: 'fixed', top: 0, right: '5%', width: '30%', height: '100%', zIndex: 1,
      background: 'radial-gradient(ellipse at 60% 0%, rgba(60,20,180,0.22) 0%, transparent 65%)',
      pointerEvents: 'none',
    }} />
    {/* White beam center — on the drummer */}
    <div style={{
      position: 'fixed', top: 0, left: '38%', width: '24%', height: '80%', zIndex: 1,
      background: 'radial-gradient(ellipse at 50% 0%, rgba(255,200,160,0.18) 0%, transparent 55%)',
      pointerEvents: 'none',
      animation: 'concertPulse 1.8s ease-in-out infinite 0.4s',
    }} />

    {/* Stage floor — dark wood planks */}
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '38%', zIndex: 2,
      background: 'repeating-linear-gradient(90deg, #1e1008 0px, #1e1008 78px, #160c06 78px, #160c06 80px)',
    }} />
    {/* Floor plank grain lines */}
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '38%', zIndex: 2,
      background: 'repeating-linear-gradient(0deg, transparent, transparent 18px, rgba(0,0,0,0.15) 18px, rgba(0,0,0,0.15) 19px)',
      pointerEvents: 'none',
    }} />
    {/* Stage edge lip */}
    <div style={{
      position: 'fixed', bottom: '38%', left: 0, right: 0, height: '6px', zIndex: 3,
      background: 'linear-gradient(180deg, #3a2010 0%, #2a1408 100%)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.9)',
    }} />

    {/* Crowd silhouettes in dark distance */}
    <div style={{
      position: 'fixed', bottom: 'calc(38% + 6px)', left: 0, right: 0, height: '28%', zIndex: 2,
      background: 'linear-gradient(180deg, transparent 0%, #0a0404 70%, #0e0606 100%)',
      pointerEvents: 'none',
    }} />
    {/* Crowd shape SVG */}
    <svg
      style={{ position: 'fixed', bottom: 'calc(38% + 6px)', left: 0, right: 0, width: '100%', height: '120px', zIndex: 2, pointerEvents: 'none' }}
      viewBox="0 0 1200 120"
      preserveAspectRatio="xMidYMax slice"
    >
      {/* Crowd silhouettes — irregular bumpy tops */}
      <path d="M 0 80 Q 20 55 40 72 Q 55 60 65 72 Q 75 52 90 68 Q 108 48 118 65 Q 130 55 145 70 Q 158 42 172 60 Q 188 50 200 68 Q 215 38 228 58 Q 242 46 255 66 Q 268 52 282 70 Q 295 40 308 60 Q 320 48 335 66 Q 348 38 360 58 Q 375 46 390 68 Q 402 52 416 70 Q 428 40 442 60 Q 455 50 468 68 Q 482 36 496 56 Q 508 46 522 64 Q 535 40 548 60 Q 562 50 576 70 Q 588 38 602 58 Q 615 46 628 66 Q 642 52 655 70 Q 668 40 682 60 Q 695 50 708 68 Q 722 36 736 56 Q 748 46 762 64 Q 775 40 788 60 Q 802 50 816 70 Q 828 38 842 58 Q 855 46 868 66 Q 882 52 895 70 Q 908 40 922 60 Q 935 50 948 68 Q 962 36 976 56 Q 988 46 1002 64 Q 1015 40 1028 60 Q 1042 50 1056 68 Q 1068 36 1082 56 Q 1095 46 1108 64 Q 1122 40 1136 60 Q 1150 48 1162 66 Q 1176 52 1190 70 L 1200 70 L 1200 120 L 0 120 Z"
        fill="#0a0404" />
      {/* Occasional raised arms/lighters */}
      {[80, 190, 310, 450, 590, 680, 820, 960, 1080].map((x, i) => (
        <g key={i}>
          <line x1={x} y1="65" x2={x} y2="42" stroke="#1a0e08" strokeWidth="3" strokeLinecap="round" />
          <circle cx={x} cy="40" r="3.5" fill={i % 3 === 0 ? '#ff8800' : i % 3 === 1 ? '#ffcc00' : '#ff4400'} opacity="0.75" />
          <circle cx={x} cy="40" r="6" fill={i % 3 === 0 ? '#ff8800' : '#ffaa00'} opacity="0.18" />
        </g>
      ))}
    </svg>

    {/* Stage haze / smoke on floor */}
    <div style={{
      position: 'fixed', bottom: 'calc(38% + 6px)', left: 0, right: 0, height: '18%', zIndex: 3,
      background: 'linear-gradient(0deg, rgba(40,18,10,0.75) 0%, rgba(25,10,5,0.45) 40%, transparent 100%)',
      pointerEvents: 'none',
      filter: 'blur(8px)',
    }} />

    {/* Stage canopy / truss shadow from top */}
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: '10%', zIndex: 4,
      background: 'linear-gradient(180deg, #0a0606 0%, rgba(10,6,6,0.8) 60%, transparent 100%)',
      pointerEvents: 'none',
    }} />
    {/* Truss beam */}
    <div style={{
      position: 'fixed', top: '9%', left: 0, right: 0, height: '5px', zIndex: 4,
      background: 'linear-gradient(90deg, #2a2020, #3a3030 20%, #4a4040 50%, #3a3030 80%, #2a2020)',
      boxShadow: '0 3px 10px rgba(0,0,0,0.9)',
    }} />
    {/* LED strip on truss */}
    <div style={{
      position: 'fixed', top: 'calc(9% + 5px)', left: 0, right: 0, height: '3px', zIndex: 4,
      background: 'repeating-linear-gradient(90deg, #cc2200 0px, #cc2200 12px, #ff6600 12px, #ff6600 24px, #cc2200 24px, #cc2200 36px, #ffcc00 36px, #ffcc00 48px)',
      opacity: 0.85,
      animation: 'concertLED 0.8s linear infinite',
    }} />
  </>
);

/* Left side: PA speaker stack + stage light */
const ConcertLeftDecor = () => (
  <div className="decor-hide" style={{
    position: 'fixed', left: 0, top: '9%', bottom: '16%',
    width: '160px', zIndex: 5,
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    padding: '0 10px', gap: '4px',
  }}>
    {/* Stage light fixture */}
    <div style={{ display: 'flex', gap: '4px', paddingLeft: '8px', marginBottom: '8px' }}>
      <StageLight color="#cc2200" angle={15} />
      <StageLight color="#ff6600" angle={-8} />
      <StageLight color="#cc2200" angle={20} />
    </div>
    {/* Hanging light beam from first light */}
    <div style={{
      marginLeft: '18px',
      width: '40px', height: '180px',
      background: 'linear-gradient(180deg, rgba(200,30,0,0.25) 0%, transparent 100%)',
      transform: 'skewX(8deg)',
      pointerEvents: 'none',
    }} />
    {/* PA speaker stacks */}
    <div style={{ display: 'flex', gap: '3px', marginTop: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <PASpeaker width={70} height={96} />
        <PASpeaker width={70} height={96} />
        <PASpeaker width={70} height={96} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '20px' }}>
        <PASpeaker width={68} height={92} />
        <PASpeaker width={68} height={92} />
      </div>
    </div>
  </div>
);

/* Right side: Marshall stack + stage light */
const ConcertRightDecor = () => (
  <div className="decor-hide" style={{
    position: 'fixed', right: 0, top: '9%', bottom: '16%',
    width: '165px', zIndex: 5,
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
    padding: '0 10px', gap: '4px',
  }}>
    {/* Stage lights */}
    <div style={{ display: 'flex', gap: '4px', paddingRight: '8px', marginBottom: '8px' }}>
      <StageLight color="#3311cc" angle={-20} />
      <StageLight color="#cc2200" angle={6} />
      <StageLight color="#aa00cc" angle={-12} />
    </div>
    {/* Hanging beam from light */}
    <div style={{
      marginRight: '22px', alignSelf: 'flex-end',
      width: '38px', height: '160px',
      background: 'linear-gradient(180deg, rgba(50,15,160,0.22) 0%, transparent 100%)',
      transform: 'skewX(-6deg)',
      pointerEvents: 'none',
    }} />
    {/* Marshall amp stacks */}
    <div style={{ display: 'flex', gap: '3px', marginTop: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '15px' }}>
        <MarshallCab width={85} height={94} />
        <MarshallCab width={85} height={94} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <MarshallCab width={88} height={96} />
        <MarshallCab width={88} height={96} />
        <MarshallCab width={88} height={96} />
      </div>
    </div>
  </div>
);

/* Bottom bar: stage floor with drum equipment and cables */
const ConcertBottomBar = () => (
  <div style={{
    position: 'fixed', bottom: 0, left: 0, right: 0, height: '62px',
    background: 'linear-gradient(180deg, #1e1008 0%, #140c06 100%)',
    borderTop: '3px solid #0a0604', zIndex: 5,
    display: 'flex', alignItems: 'center', padding: '0 24px', gap: '16px',
    boxShadow: '0 -6px 20px rgba(0,0,0,0.8)',
  }}>
    {/* Floor monitor wedge (SVG) */}
    <svg viewBox="0 0 80 34" width="80" height="34" style={{ display: 'block', flexShrink: 0 }}>
      <path d="M 0 32 L 70 32 L 70 12 L 50 2 L 8 2 L 0 12 Z"
        fill="#1a1a18" stroke="#2a2a28" strokeWidth="1.5" />
      <rect x="8" y="6" width="56" height="22" rx="2"
        fill="#111110" stroke="#222220" strokeWidth="1" />
      <circle cx="36" cy="17" r="9" fill="#0a0a08" stroke="#1e1e1c" strokeWidth="1.5" />
      <circle cx="36" cy="17" r="5" fill="#060604" />
      <circle cx="36" cy="17" r="2" fill="#141410" />
      <rect x="14" y="28" width="52" height="4" rx="1" fill="#cc2200" opacity="0.7" />
    </svg>
    {/* Cables on floor */}
    <svg viewBox="0 0 120 16" width="120" height="16" style={{ display: 'block', flexShrink: 0 }}>
      <path d="M 0 8 Q 20 2 40 10 Q 60 16 80 6 Q 100 0 120 10"
        fill="none" stroke="#2a2218" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M 0 12 Q 30 6 55 14 Q 75 18 100 8 Q 110 4 120 12"
        fill="none" stroke="#181410" strokeWidth="3" strokeLinecap="round" />
    </svg>
    {/* Drum items emoji row */}
    <div style={{ marginLeft: 'auto', display: 'flex', gap: '14px', fontSize: '1.3rem' }}>
      <span>🥁</span><span>🎸</span><span>🎵</span>
    </div>
    {/* Concert info tag */}
    <div style={{
      background: '#cc2200', color: 'white',
      padding: '3px 10px', fontSize: '7px', fontWeight: 'bold',
      fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em',
      border: '1px solid #ff3300', boxShadow: '0 0 8px rgba(204,34,0,0.6)',
    }}>
      LIVE
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   WATCHMAKER THEME — old Swiss watch shop
══════════════════════════════════════════════════ */

/* Small SVG Swiss flag */
const SwissFlag = ({ size = 36, style: flagStyle = {} }) => (
  <svg viewBox="0 0 36 36" width={size} height={size} style={{ display: 'block', ...flagStyle }}>
    <rect width="36" height="36" fill="#dc0000" rx="2" />
    <rect x="14" y="7" width="8" height="22" fill="white" />
    <rect x="7" y="14" width="22" height="8" fill="white" />
  </svg>
);

/* Round wall clock (warm wood case) */
const WallClockWM = ({ size = 60, style: clockStyle = {} }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} style={{ display: 'block', ...clockStyle }}>
    <circle cx="33" cy="33" r="30" fill="rgba(0,0,0,0.2)" />
    <circle cx="32" cy="32" r="30" fill="#5a3a10" stroke="#3a2208" strokeWidth="2.5" />
    <circle cx="32" cy="32" r="26" fill="none" stroke="#7a5020" strokeWidth="1.5" />
    <circle cx="32" cy="32" r="25" fill="#f8f2e0" stroke="#c8a040" strokeWidth="1.5" />
    <circle cx="32" cy="32" r="23" fill="none" stroke="rgba(100,70,30,0.15)" strokeWidth="0.8" />
    {[...Array(12)].map((_, i) => {
      const a = (i * 30 - 90) * Math.PI / 180;
      const r1 = i % 3 === 0 ? 16 : 19;
      return <line key={i}
        x1={32 + r1 * Math.cos(a)} y1={32 + r1 * Math.sin(a)}
        x2={32 + 22 * Math.cos(a)} y2={32 + 22 * Math.sin(a)}
        stroke="#4a2808" strokeWidth={i % 3 === 0 ? 2.5 : 1.5} />;
    })}
    <text x="32" y="14" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">XII</text>
    <text x="49" y="35" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">III</text>
    <text x="32" y="53" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">VI</text>
    <text x="15" y="35" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">IX</text>
    <line x1="32" y1="32" x2="24" y2="18" stroke="#2a1408" strokeWidth="3" strokeLinecap="round" />
    <line x1="32" y1="32" x2="38" y2="11" stroke="#2a1408" strokeWidth="2" strokeLinecap="round" />
    <line x1="32" y1="32" x2="32" y2="10" stroke="#dc0000" strokeWidth="1" strokeLinecap="round" />
    <circle cx="32" cy="32" r="2.5" fill="#5a3810" />
  </svg>
);

/* Pocket watch hanging on hook */
const HangingPocketWatch = ({ style: watchStyle = {} }) => (
  <svg viewBox="0 0 50 72" width="50" height="72" style={{ display: 'block', ...watchStyle }}>
    <path d="M 25 0 Q 28 0 28 4 Q 28 7 25 7" fill="none" stroke="#c8a820" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="25" y1="7" x2="25" y2="16" stroke="#c8a820" strokeWidth="1.5" strokeDasharray="2,1.5" />
    <rect x="21" y="16" width="8" height="5" rx="2" fill="#c8a820" stroke="#9a7810" strokeWidth="1" />
    <circle cx="25" cy="44" r="22" fill="#d4a820" stroke="#9a7010" strokeWidth="2" />
    <circle cx="25" cy="44" r="19" fill="#f8f2e0" stroke="#c8a030" strokeWidth="1.5" />
    {[...Array(12)].map((_, i) => {
      const a = (i * 30 - 90) * Math.PI / 180;
      return <line key={i}
        x1={25 + 14 * Math.cos(a)} y1={44 + 14 * Math.sin(a)}
        x2={25 + 18 * Math.cos(a)} y2={44 + 18 * Math.sin(a)}
        stroke="#4a2808" strokeWidth={i % 3 === 0 ? 2 : 1} />;
    })}
    <line x1="25" y1="44" x2="21" y2="35" stroke="#1a0c08" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="25" y1="44" x2="31" y2="30" stroke="#1a0c08" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="25" cy="44" r="2" fill="#c8a820" />
  </svg>
);

/* Cuckoo clock */
const CuckooClockWM = ({ width = 82, height = 148 }) => (
  <svg viewBox="0 0 82 148" width={width} height={height} style={{ display: 'block' }}>
    <rect x="11" y="56" width="60" height="70" rx="2" fill="#4a2e10" stroke="#2a1a08" strokeWidth="2" />
    {/* Chalet roof */}
    <path d="M 6 56 L 41 10 L 76 56 Z" fill="#3a2008" stroke="#2a1408" strokeWidth="2" />
    <path d="M 14 56 L 41 18 L 68 56 Z" fill="#5a3818" />
    {/* Gable decoration */}
    <path d="M 34 28 Q 41 20 48 28 Q 44 34 41 30 Q 38 34 34 28 Z" fill="#2e1808" />
    {/* Front gable arch */}
    <path d="M 36 24 Q 41 18 46 24 L 46 30 L 36 30 Z" fill="#6a3818" />
    {/* Carved roof trim */}
    <path d="M 6 56 L 10 48 L 14 54 L 18 46 L 22 52 L 26 44 L 30 50 L 34 42 L 38 48 L 41 40 L 44 48 L 48 42 L 52 50 L 56 44 L 60 52 L 64 46 L 68 54 L 72 48 L 76 56"
      fill="none" stroke="#2a1408" strokeWidth="1.5" />
    {/* Cuckoo door */}
    <rect x="31" y="58" width="20" height="14" rx="1" fill="#6a3a18" stroke="#3a1e08" strokeWidth="1.5" />
    {/* Clock face */}
    <circle cx="41" cy="90" r="20" fill="#f8f2e0" stroke="#8b6030" strokeWidth="2.5" />
    <circle cx="41" cy="90" r="18" fill="none" stroke="rgba(100,70,30,0.15)" strokeWidth="0.8" />
    {[...Array(12)].map((_, i) => {
      const a = (i * 30 - 90) * Math.PI / 180;
      const r1 = i % 3 === 0 ? 11 : 14;
      return <line key={i}
        x1={41 + r1 * Math.cos(a)} y1={90 + r1 * Math.sin(a)}
        x2={41 + 17 * Math.cos(a)} y2={90 + 17 * Math.sin(a)}
        stroke="#4a2808" strokeWidth={i % 3 === 0 ? 2.5 : 1.5} />;
    })}
    <text x="41" y="78" textAnchor="middle" fontSize="5.5" fill="#3a2010" fontFamily="serif">XII</text>
    <text x="57" y="93" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">III</text>
    <text x="41" y="107" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">VI</text>
    <text x="25" y="93" textAnchor="middle" fontSize="5" fill="#3a2010" fontFamily="serif">IX</text>
    <line x1="41" y1="90" x2="35" y2="80" stroke="#2a1408" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="41" y1="90" x2="48" y2="74" stroke="#2a1408" strokeWidth="2" strokeLinecap="round" />
    <line x1="41" y1="90" x2="41" y2="75" stroke="#dc0000" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="41" cy="90" r="2.5" fill="#5a3810" />
    {/* Pendulum */}
    <line x1="41" y1="110" x2="41" y2="135" stroke="#8b6030" strokeWidth="2" style={{ transformOrigin: '41px 110px', animation: 'pendulumSwing 1.2s ease-in-out infinite' }} />
    <ellipse cx="41" cy="139" rx="11" ry="7" fill="#c8a820" stroke="#9a7810" strokeWidth="1.8" style={{ transformOrigin: '41px 110px', animation: 'pendulumSwing 1.2s ease-in-out infinite' }} />
    <ellipse cx="38" cy="137" rx="4.5" ry="2.5" fill="rgba(255,220,120,0.38)" />
    {/* Pine cone weights */}
    <rect x="22" y="112" width="9" height="18" rx="3" fill="#5a3810" stroke="#3a2008" strokeWidth="1.5" />
    <rect x="51" y="118" width="9" height="18" rx="3" fill="#5a3810" stroke="#3a2008" strokeWidth="1.5" />
    <line x1="26" y1="112" x2="26" y2="128" stroke="#a07820" strokeWidth="1.5" strokeDasharray="2,2" />
    <line x1="55" y1="118" x2="55" y2="134" stroke="#a07820" strokeWidth="1.5" strokeDasharray="2,2" />
    {/* Carved leaf details */}
    <path d="M 11 118 Q 8 122 11 126 Q 14 122 11 118 Z" fill="#3a2008" />
    <path d="M 71 118 Q 74 122 71 126 Q 68 122 71 118 Z" fill="#3a2008" />
  </svg>
);

/* Glass display case with watches */
const WatchDisplayCase = () => (
  <svg viewBox="0 0 148 108" width="148" height="108" style={{ display: 'block' }}>
    <rect x="2" y="24" width="144" height="80" rx="3" fill="#3a2410" stroke="#2a1808" strokeWidth="2" />
    <rect x="2" y="8" width="144" height="20" rx="2" fill="#2e1c0a" stroke="#5a3818" strokeWidth="1.5" />
    <rect x="62" y="4" width="24" height="8" rx="3" fill="#5a3818" stroke="#3a2408" strokeWidth="1" />
    {/* Glass pane */}
    <rect x="5" y="27" width="138" height="66" rx="2"
      fill="rgba(180,200,220,0.08)" stroke="rgba(200,215,230,0.2)" strokeWidth="1" />
    <path d="M 5 27 L 60 27 L 5 80 Z" fill="rgba(255,255,255,0.04)" />
    {/* Velvet lining */}
    <rect x="8" y="30" width="132" height="60" rx="1" fill="#1a0828" />
    <line x1="50" y1="30" x2="50" y2="90" stroke="rgba(80,40,80,0.35)" strokeWidth="0.8" />
    <line x1="98" y1="30" x2="98" y2="90" stroke="rgba(80,40,80,0.35)" strokeWidth="0.8" />
    {/* Gold pocket watch */}
    <circle cx="28" cy="60" r="14" fill="#d4a820" stroke="#9a7010" strokeWidth="2" />
    <circle cx="28" cy="60" r="11" fill="#f8f2e0" />
    {[0,3,6,9].map(i => {
      const a = (i * 90 - 90) * Math.PI / 180;
      return <line key={i} x1={28 + 7*Math.cos(a)} y1={60 + 7*Math.sin(a)}
        x2={28 + 10*Math.cos(a)} y2={60 + 10*Math.sin(a)} stroke="#4a2808" strokeWidth="1.8" />;
    })}
    <line x1="28" y1="60" x2="24" y2="53" stroke="#1a0c08" strokeWidth="2" strokeLinecap="round" />
    <line x1="28" y1="60" x2="33" y2="52" stroke="#1a0c08" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="28" cy="60" r="1.8" fill="#c8a820" />
    <rect x="25" y="46" width="6" height="4" rx="1.5" fill="#c8a820" />
    <text x="28" y="86" textAnchor="middle" fontSize="4" fill="#d4a820" fontFamily="serif" fontStyle="italic">1890</text>
    {/* Wristwatch on cushion */}
    <ellipse cx="74" cy="63" rx="16" ry="10" fill="#2a1840" />
    <rect x="64" y="50" width="20" height="6" rx="2" fill="#2a1808" />
    <rect x="64" y="72" width="20" height="6" rx="2" fill="#2a1808" />
    <rect x="60" y="54" width="28" height="20" rx="4" fill="#d4a820" stroke="#9a7010" strokeWidth="1.5" />
    <rect x="62" y="56" width="24" height="16" rx="3" fill="#f0e8d0" />
    <line x1="74" y1="64" x2="70" y2="59" stroke="#2a1408" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="74" y1="64" x2="78" y2="58" stroke="#2a1408" strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="74" cy="64" r="1.5" fill="#c8a820" />
    <text x="74" y="86" textAnchor="middle" fontSize="4" fill="#d4a820" fontFamily="serif" fontStyle="italic">Swiss Made</text>
    {/* Silver pocket watch */}
    <circle cx="120" cy="60" r="14" fill="#c8c8c8" stroke="#888" strokeWidth="2" />
    <circle cx="120" cy="60" r="11" fill="#f0f0f0" />
    {[0,3,6,9].map(i => {
      const a = (i * 90 - 90) * Math.PI / 180;
      return <line key={i} x1={120 + 7*Math.cos(a)} y1={60 + 7*Math.sin(a)}
        x2={120 + 10*Math.cos(a)} y2={60 + 10*Math.sin(a)} stroke="#444" strokeWidth="1.8" />;
    })}
    <line x1="120" y1="60" x2="116" y2="53" stroke="#333" strokeWidth="2" strokeLinecap="round" />
    <line x1="120" y1="60" x2="125" y2="52" stroke="#333" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="120" cy="60" r="1.8" fill="#b0b0b0" />
    <rect x="117" y="46" width="6" height="4" rx="1.5" fill="#c0c0c0" />
    <text x="120" y="86" textAnchor="middle" fontSize="4" fill="#d0d0d0" fontFamily="serif" fontStyle="italic">Argent</text>
    {/* Label strip */}
    <rect x="2" y="98" width="144" height="10" rx="0" fill="#2a1808" />
    <text x="74" y="106" textAnchor="middle" fontSize="5.5" fill="#d4a820" fontFamily="Georgia, serif" fontStyle="italic">Swiss Masterpiece Timepieces</text>
  </svg>
);

const WatchmakerBackground = () => (
  <>
    {/* Base walnut paneling */}
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: '#2a1808' }} />
    {/* Upper wall — vertical planks */}
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: '57%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, #2e1c0a 0px, #2e1c0a 78px, #261608 78px, #261608 80px)',
    }} />
    {/* Wood grain shimmer */}
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: '57%', zIndex: 1,
      background: 'repeating-linear-gradient(92deg, transparent 0px, rgba(255,150,50,0.025) 20px, transparent 40px)',
      pointerEvents: 'none',
    }} />
    {/* Chair rail / wainscoting divider */}
    <div style={{
      position: 'fixed', top: 'calc(57% - 3px)', left: 0, right: 0, height: '9px', zIndex: 2,
      background: 'linear-gradient(180deg, #9a6030 0%, #7a4820 50%, #5a3010 100%)',
      boxShadow: '0 2px 10px rgba(0,0,0,0.5), 0 -1px 4px rgba(0,0,0,0.3)',
    }} />
    {/* Lower wainscoting — raised panel effect */}
    <div style={{
      position: 'fixed', top: 'calc(57% + 6px)', left: 0, right: 0, bottom: '6%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, #3a2210 0px, #3a2210 118px, #2e1808 118px, #2e1808 120px)',
    }} />
    <div style={{
      position: 'fixed', top: 'calc(57% + 10px)', left: 0, right: 0, bottom: '7%', zIndex: 1,
      background: 'repeating-linear-gradient(90deg, transparent 8px, transparent 48px, rgba(0,0,0,0.12) 48px, rgba(0,0,0,0.12) 70px, transparent 70px, transparent 80px)',
      pointerEvents: 'none',
    }} />
    {/* Floorboards */}
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '6%', zIndex: 2,
      background: 'repeating-linear-gradient(90deg, #5a3a18 0px, #5a3a18 88px, #4a2e10 88px, #4a2e10 90px)',
      borderTop: '3px solid #7a5028',
    }} />
    <div style={{
      position: 'fixed', bottom: '5.5%', left: 0, right: 0, height: '4px', zIndex: 2,
      background: 'linear-gradient(180deg, rgba(180,120,50,0.28) 0%, transparent 100%)',
      pointerEvents: 'none',
    }} />
    {/* Warm amber overhead glow */}
    <div style={{
      position: 'fixed', top: 0, left: '20%', width: '60%', height: '70%', zIndex: 3,
      background: 'radial-gradient(ellipse at 50% 0%, rgba(255,195,70,0.22) 0%, transparent 65%)',
      pointerEvents: 'none',
    }} />
    {/* Left sconce glow */}
    <div style={{
      position: 'fixed', top: '5%', left: 0, width: '32%', height: '65%', zIndex: 3,
      background: 'radial-gradient(ellipse at 10% 30%, rgba(210,140,40,0.16) 0%, transparent 55%)',
      pointerEvents: 'none',
    }} />
    {/* Right sconce glow */}
    <div style={{
      position: 'fixed', top: '5%', right: 0, width: '32%', height: '65%', zIndex: 3,
      background: 'radial-gradient(ellipse at 90% 30%, rgba(210,140,40,0.14) 0%, transparent 55%)',
      pointerEvents: 'none',
    }} />
    {/* Center beam on character */}
    <div style={{
      position: 'fixed', top: 0, left: '35%', width: '30%', height: '80%', zIndex: 3,
      background: 'radial-gradient(ellipse at 50% 0%, rgba(255,210,120,0.14) 0%, transparent 55%)',
      pointerEvents: 'none',
    }} />
    {/* Gas lamp fixture (ceiling) */}
    <div style={{
      position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)', width: '6px', height: '20px',
      background: '#5a3818', zIndex: 4, borderRadius: '0 0 3px 3px',
    }} />
    <div style={{
      position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)',
      width: '32px', height: '16px', zIndex: 4,
      background: 'radial-gradient(ellipse at 50% 80%, rgba(255,210,100,0.75) 0%, rgba(255,180,60,0.4) 55%, transparent 100%)',
      borderRadius: '50%',
    }} />
  </>
);

/* Left side: Cuckoo clock + clocks + Swiss flag + shop sign */
const WatchshopLeftDecor = () => (
  <div className="decor-hide" style={{
    position: 'fixed', left: '6px', top: '50%', transform: 'translateY(-50%)',
    zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
  }}>
    <CuckooClockWM width={80} height={145} />
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <WallClockWM size={48} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
        <HangingPocketWatch />
        <SwissFlag size={28} />
      </div>
    </div>
    <div style={{
      background: 'linear-gradient(180deg, #5a3818 0%, #3a2008 100%)',
      border: '2px solid #9a7030', padding: '5px 12px',
      color: '#e8c060', fontSize: '7px', fontFamily: 'Georgia, serif', fontStyle: 'italic',
      textAlign: 'center', letterSpacing: '0.1em',
      boxShadow: '2px 2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,200,80,0.12)',
      whiteSpace: 'nowrap',
    }}>
      MEIER &amp; SÖHNE
      <div style={{ fontSize: '5.5px', color: '#c8a040', marginTop: '2px', fontStyle: 'normal', letterSpacing: '0.08em' }}>
        HORLOGERS — GENÈVE  EST. 1878
      </div>
    </div>
  </div>
);

/* Right side: Display case + wall clock + flags */
const WatchshopRightDecor = () => (
  <div className="decor-hide" style={{
    position: 'fixed', right: '6px', top: '50%', transform: 'translateY(-50%)',
    zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
  }}>
    <WallClockWM size={72} />
    <div style={{ display: 'flex', gap: '8px' }}>
      <SwissFlag size={28} />
      <SwissFlag size={28} />
    </div>
    <WatchDisplayCase />
    <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
      <HangingPocketWatch />
      <HangingPocketWatch />
    </div>
  </div>
);

/* Bottom bar: Watchmaker's workbench */
const WatchshopBottomBar = () => (
  <div style={{
    position: 'fixed', bottom: 0, left: 0, right: 0, height: '60px',
    background: 'linear-gradient(180deg, #5a3a18 0%, #4a2e10 100%)',
    borderTop: '3px solid #3a2008', zIndex: 5,
    display: 'flex', alignItems: 'center', padding: '0 24px', gap: '14px',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.5)',
  }}>
    {/* Green leather watchmaking mat + tools */}
    <svg viewBox="0 0 110 42" width="110" height="42" style={{ display: 'block', flexShrink: 0 }}>
      <rect x="2" y="5" width="106" height="33" rx="4" fill="#1a3818" stroke="#0e2410" strokeWidth="1.5" />
      <rect x="4" y="7" width="102" height="29" rx="3" fill="#1e4020" />
      <rect x="4" y="7" width="102" height="29" rx="3" fill="none" stroke="rgba(100,80,40,0.28)" strokeWidth="1.5" strokeDasharray="3,3" />
      {/* Tweezers */}
      <line x1="14" y1="10" x2="10" y2="34" stroke="#c0c8d0" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="20" y1="10" x2="16" y2="34" stroke="#c0c8d0" strokeWidth="2" strokeLinecap="round" />
      {/* Screwdriver */}
      <line x1="34" y1="8" x2="34" y2="32" stroke="#c8b040" strokeWidth="2" strokeLinecap="round" />
      <rect x="30" y="8" width="8" height="9" rx="2" fill="#8b3010" stroke="#6a2008" strokeWidth="0.8" />
      {/* Loupe on mat */}
      <circle cx="54" cy="21" r="9" fill="none" stroke="#c8a830" strokeWidth="2.2" />
      <circle cx="54" cy="21" r="7.5" fill="rgba(180,220,255,0.14)" />
      <ellipse cx="50" cy="17" rx="3.5" ry="2.5" fill="rgba(255,255,255,0.2)" transform="rotate(-20,50,17)" />
      <line x1="63" y1="29" x2="70" y2="37" stroke="#c8a830" strokeWidth="2.5" strokeLinecap="round" />
      {/* Mainspring coil */}
      <path d="M 82 21 Q 84 17 88 18 Q 91 19 91 23 Q 91 27 87 28 Q 83 28 82 24 Q 82 20 85 19"
        fill="none" stroke="#b8b8b8" strokeWidth="1.5" strokeLinecap="round" />
      {/* Escapement gear */}
      {[0,1,2,3,4,5,6,7].map(i => {
        const a = (i * 45) * Math.PI / 180;
        return <line key={i}
          x1={102 + 5*Math.cos(a)} y1={21 + 5*Math.sin(a)}
          x2={102 + 7*Math.cos(a)} y2={21 + 7*Math.sin(a)}
          stroke="#c8a820" strokeWidth="2" strokeLinecap="round" />;
      })}
      <circle cx="102" cy="21" r="5" fill="none" stroke="#c8a820" strokeWidth="1.5" />
      <circle cx="102" cy="21" r="2.5" fill="#d4a820" />
    </svg>
    <div style={{ display: 'flex', gap: '12px', fontSize: '1.4rem' }}>
      <span>⌚</span><span>🔍</span><span>⚙️</span>
    </div>
    <div style={{
      marginLeft: 'auto',
      background: 'linear-gradient(135deg, #8a0a14 0%, #5a0810 100%)',
      color: 'white', padding: '5px 12px',
      border: '1px solid #dc0000',
      fontSize: '7px', fontFamily: 'Georgia, serif',
      textAlign: 'center', letterSpacing: '0.06em',
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
    }}>
      🇨🇭 SWISS MADE
      <div style={{ color: '#ffd700', fontSize: '6px', marginTop: '1px' }}>Précision Absolue</div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   RESULT OVERLAY — level-aware
══════════════════════════════════════════════════ */
const RESULT_COPY = {
  doctor: {
    victoryEmoji: '🏆', victoryTitle: 'VICTORY!', victorySub: 'Dr. Tempus is impressed!',
    defeatEmoji: '💊', defeatTitle: 'DEFEAT', defeatSub: 'The doctor suggests more practice…',
  },
  rider: {
    victoryEmoji: '🍕', victoryTitle: 'ON TIME!', victorySub: 'Pizza delivered fresh!',
    defeatEmoji: '🛵', defeatTitle: 'TOO LATE', defeatSub: 'Pizza went cold… try again.',
  },
  baker: {
    victoryEmoji: '🥧', victoryTitle: 'PERFECTLY BAKED!', victorySub: 'Not a single burned cake!',
    defeatEmoji: '🔥', defeatTitle: 'OVERBAKED!', defeatSub: 'The timer was off… try again.',
  },
  traindriver: {
    victoryEmoji: '🚄', victoryTitle: 'ON SCHEDULE!', victorySub: 'Shinkansen precision achieved!',
    defeatEmoji: '⏰', defeatTitle: 'DELAYED!', defeatSub: 'The Shinkansen waits for no one…',
  },
  bonzo: {
    victoryEmoji: '🥁', victoryTitle: 'ROCK SOLID!', victorySub: 'Even Bonzo tips his sticks to you!',
    defeatEmoji: '🎵', defeatTitle: 'OFF BEAT!', defeatSub: 'Not quite Bonham timing…',
  },
  watchmaker: {
    victoryEmoji: '⌚', victoryTitle: 'MASTERWORK!', victorySub: 'Klaus Meier nods in approval!',
    defeatEmoji: '⚙️', defeatTitle: 'IMPRECISE!', defeatSub: 'A watchmaker cannot tolerate such slop…',
  },
};

const ResultOverlay = ({ mae, target, levelId, onRetry, onExit, onNext, hasNext }) => {
  const passed = mae <= target;
  const copy = RESULT_COPY[levelId] || RESULT_COPY.doctor;
  return (
    <div style={overlayStyles.backdrop}>
      <div style={{ ...overlayStyles.card, borderColor: passed ? '#4caf50' : '#f44336', boxShadow: passed ? '0 0 60px rgba(76,175,80,0.5)' : '0 0 60px rgba(244,67,54,0.5)' }}>
        {passed ? (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '8px' }}>{copy.victoryEmoji}</div>
            <div style={{ ...overlayStyles.resultTitle, color: '#4caf50' }}>{copy.victoryTitle}</div>
            <div style={overlayStyles.resultSub}>{copy.victorySub}</div>
            <div style={{ ...overlayStyles.maeDisplay, background: 'rgba(76,175,80,0.15)', border: '2px solid #4caf50' }}>
              <span style={overlayStyles.maeLabel}>YOUR MAE</span>
              <span style={{ ...overlayStyles.maeVal, color: '#4caf50' }}>{mae.toFixed(1)} ms</span>
              <span style={{ ...overlayStyles.maeLabel, color: 'rgba(255,255,255,0.5)' }}>TARGET ≤ {target} ms ✓</span>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '8px' }}>{copy.defeatEmoji}</div>
            <div style={{ ...overlayStyles.resultTitle, color: '#f44336' }}>{copy.defeatTitle}</div>
            <div style={overlayStyles.resultSub}>{copy.defeatSub}</div>
            <div style={{ ...overlayStyles.maeDisplay, background: 'rgba(244,67,54,0.12)', border: '2px solid #f44336' }}>
              <span style={overlayStyles.maeLabel}>YOUR MAE</span>
              <span style={{ ...overlayStyles.maeVal, color: '#f44336' }}>{mae.toFixed(1)} ms</span>
              <span style={{ ...overlayStyles.maeLabel, color: 'rgba(255,255,255,0.5)' }}>TARGET ≤ {target} ms ✗</span>
            </div>
          </>
        )}
        <div style={overlayStyles.btnRow}>
          <button style={{ ...overlayStyles.btn, ...overlayStyles.btnSecondary }} onClick={onRetry}>↺ RETRY</button>
          {passed && hasNext && (
            <button style={{ ...overlayStyles.btn, ...overlayStyles.btnPrimary }} onClick={onNext}>NEXT LEVEL ▶</button>
          )}
          <button style={{ ...overlayStyles.btn, ...overlayStyles.btnSecondary }} onClick={onExit}>✕ EXIT</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   LEVEL THEME CONFIG
══════════════════════════════════════════════════ */
const THEMES = {
  doctor: {
    Background: DoctorBackground,
    LeftDecor: DoctorLeftDecor,
    RightDecor: DoctorRightDecor,
    BottomBar: DoctorBottomBar,
    panelBorder: '3px solid #c8b090',
    panelBg: '#fffef8',
    headerBg: 'linear-gradient(135deg, #c0392b 0%, #922b21 100%)',
    headerBorder: '2px solid #7b241c',
    headerIcon: '✚',
    panelTitleMain: 'TIMING CHECKUP',
    getPanelTitleSub: (level) => `Dr. Tempus's Office — Level ${level.levelNumber}`,
    getIdleMsg: () => 'Dr. Tempus is waiting… press START',
    challengeLabelColor: '#7a5a30',
    challengeTargetColor: '#c0392b',
    challengeBarBg: '#f0ead8',
    challengeBarBorder: '1px dashed #c0a860',
    statusColor: '#6a5030',
    statusFontStyle: 'italic',
    statusFontFamily: 'Georgia, serif',
    clipTop: true,
    clickBtnBg: 'linear-gradient(135deg, #e53935 0%, #b71c1c 100%)',
    clickBtnBorder: '3px solid #7b1f0a',
    clickBtnShadow: '0 6px 0 #7b1f0a, 0 0 20px rgba(229,57,53,0.4)',
    clickPulseKf: '0 6px 0 #7b1f0a, 0 0 35px rgba(229,57,53,0.7)',
    retryBtnBorder: '1px solid #c0a860',
    retryBtnColor: '#7a6030',
    marginBottom: '55px',
  },
  rider: {
    Background: RiderBackground,
    LeftDecor: PizzeriaFront,
    RightDecor: StreetRight,
    BottomBar: RiderBottomBar,
    panelBorder: '3px solid #2a8c00',
    panelBg: '#fefff8',
    headerBg: 'linear-gradient(135deg, #1a6a00 0%, #0f4800 100%)',
    headerBorder: '2px solid #0a3200',
    headerIcon: '🛵',
    panelTitleMain: 'TIMING DELIVERY',
    getPanelTitleSub: (level) => `Rush Eats — Level ${level.levelNumber}`,
    getIdleMsg: () => '🍕 Your order is placed… press START',
    challengeLabelColor: '#2a5a00',
    challengeTargetColor: '#1a7a00',
    challengeBarBg: '#f0f8e8',
    challengeBarBorder: '1px dashed #6ab840',
    statusColor: '#2a5a00',
    statusFontStyle: 'normal',
    statusFontFamily: "'Press Start 2P', monospace",
    clipTop: false,
    clickBtnBg: 'linear-gradient(135deg, #2a9a00 0%, #1a6a00 100%)',
    clickBtnBorder: '3px solid #0f4a00',
    clickBtnShadow: '0 6px 0 #0f4a00, 0 0 20px rgba(42,154,0,0.5)',
    clickPulseKf: '0 6px 0 #0f4a00, 0 0 35px rgba(42,154,0,0.8)',
    retryBtnBorder: '1px solid #6ab840',
    retryBtnColor: '#2a6a00',
    marginBottom: '0px',
  },
  baker: {
    Background: BakeryBackground,
    LeftDecor: BakeryLeftDecor,
    RightDecor: BakeryShelf,
    BottomBar: BakeryBottomBar,
    panelBorder: '3px solid #c8a060',
    panelBg: '#fffcf0',
    headerBg: 'linear-gradient(135deg, #c87820 0%, #904808 100%)',
    headerBorder: '2px solid #6a3206',
    headerIcon: '🥧',
    panelTitleMain: 'TIMING RECIPE',
    getPanelTitleSub: (level) => `The Bakery — Level ${level.levelNumber}`,
    getIdleMsg: () => '🥧 The oven is preheated… press START',
    challengeLabelColor: '#7a4a18',
    challengeTargetColor: '#b06010',
    challengeBarBg: '#fdf4e0',
    challengeBarBorder: '1px dashed #d4a840',
    statusColor: '#6a3c10',
    statusFontStyle: 'italic',
    statusFontFamily: 'Georgia, serif',
    clipTop: false,
    clickBtnBg: 'linear-gradient(135deg, #d4870a 0%, #9a5006 100%)',
    clickBtnBorder: '3px solid #6a3004',
    clickBtnShadow: '0 6px 0 #6a3004, 0 0 20px rgba(212,135,10,0.5)',
    clickPulseKf: '0 6px 0 #6a3004, 0 0 35px rgba(212,135,10,0.8)',
    retryBtnBorder: '1px solid #d4a840',
    retryBtnColor: '#7a4a18',
    marginBottom: '52px',
  },
  traindriver: {
    Background: TrainBackground,
    LeftDecor: StationLeftDecor,
    RightDecor: StationRightDecor,
    BottomBar: StationBottomBar,
    panelBorder: '3px solid #4a80c8',
    panelBg: '#f0f4ff',
    headerBg: 'linear-gradient(135deg, #1e4a8a 0%, #0e2858 100%)',
    headerBorder: '2px solid #0a1c40',
    headerIcon: '🚄',
    panelTitleMain: 'TIMING DEPARTURE',
    getPanelTitleSub: (level) => `JR Shinkansen — Level ${level.levelNumber}`,
    getIdleMsg: () => '🚄 Platform ready… press START',
    challengeLabelColor: '#1a3870',
    challengeTargetColor: '#1e4a8a',
    challengeBarBg: '#eef2ff',
    challengeBarBorder: '1px dashed #8ab4e8',
    statusColor: '#1a3870',
    statusFontStyle: 'normal',
    statusFontFamily: "'Press Start 2P', monospace",
    clipTop: false,
    clickBtnBg: 'linear-gradient(135deg, #1e5ab4 0%, #0e2e7a 100%)',
    clickBtnBorder: '3px solid #081840',
    clickBtnShadow: '0 6px 0 #081840, 0 0 20px rgba(30,74,138,0.5)',
    clickPulseKf: '0 6px 0 #081840, 0 0 35px rgba(30,74,138,0.8)',
    retryBtnBorder: '1px solid #8ab4e8',
    retryBtnColor: '#1a3870',
    marginBottom: '58px',
  },
  bonzo: {
    Background: ConcertBackground,
    LeftDecor: ConcertLeftDecor,
    RightDecor: ConcertRightDecor,
    BottomBar: ConcertBottomBar,
    panelBorder: '3px solid #cc3300',
    panelBg: '#0d0a08',
    headerBg: 'linear-gradient(135deg, #7a0a00 0%, #480600 100%)',
    headerBorder: '2px solid #3a0400',
    headerIcon: '🥁',
    panelTitleMain: 'TIMING THE BEAT',
    getPanelTitleSub: (level) => `Live Concert — Level ${level.levelNumber}`,
    getIdleMsg: () => '🥁 The crowd is waiting… press START',
    challengeLabelColor: '#cc6644',
    challengeTargetColor: '#ff4422',
    challengeBarBg: '#180a08',
    challengeBarBorder: '1px dashed #cc3300',
    statusColor: '#dd6644',
    statusFontStyle: 'normal',
    statusFontFamily: "'Press Start 2P', monospace",
    clipTop: false,
    clickBtnBg: 'linear-gradient(135deg, #cc2200 0%, #880e00 100%)',
    clickBtnBorder: '3px solid #440800',
    clickBtnShadow: '0 6px 0 #440800, 0 0 20px rgba(200,34,0,0.6)',
    clickPulseKf: '0 6px 0 #440800, 0 0 50px rgba(255,60,0,1)',
    retryBtnBorder: '1px solid #cc3300',
    retryBtnColor: '#cc6644',
    marginBottom: '62px',
  },
  watchmaker: {
    Background: WatchmakerBackground,
    LeftDecor: WatchshopLeftDecor,
    RightDecor: WatchshopRightDecor,
    BottomBar: WatchshopBottomBar,
    panelBorder: '3px solid #c8a820',
    panelBg: '#fdfbf2',
    headerBg: 'linear-gradient(135deg, #6a4010 0%, #3a2008 100%)',
    headerBorder: '2px solid #2a1408',
    headerIcon: '⌚',
    panelTitleMain: 'TIMING THE MECHANISM',
    getPanelTitleSub: (level) => `Watch Shop — Level ${level.levelNumber}`,
    getIdleMsg: () => '⌚ The mainspring is wound… press START',
    challengeLabelColor: '#5a3408',
    challengeTargetColor: '#9a6010',
    challengeBarBg: '#fef8e8',
    challengeBarBorder: '1px dashed #c8a840',
    statusColor: '#5a3408',
    statusFontStyle: 'italic',
    statusFontFamily: 'Georgia, serif',
    clipTop: false,
    clickBtnBg: 'linear-gradient(135deg, #c8820a 0%, #804806 100%)',
    clickBtnBorder: '3px solid #4a2a04',
    clickBtnShadow: '0 6px 0 #4a2a04, 0 0 20px rgba(200,130,10,0.5)',
    clickPulseKf: '0 6px 0 #4a2a04, 0 0 35px rgba(200,130,10,0.8)',
    retryBtnBorder: '1px solid #c8a840',
    retryBtnColor: '#6a4010',
    marginBottom: '62px',
  },
};

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
const ArcadeGame = () => {
  const { levelId = 'doctor' } = useParams();
  const level = getLevelById(levelId) || ARCADE_LEVELS[0];
  const navigate = useNavigate();
  const theme = THEMES[levelId] || THEMES.doctor;

  const [result, setResult] = useState(null);

  const modeConfig = useMemo(() => createFiniteMode(level.timeHorizon), [level.timeHorizon]);

  const handleGameOver = useCallback((score) => {
    const passed = score.mae <= level.targetMAE;
    setResult({ mae: score.mae, passed });
    if (passed) {
      localStorage.setItem(`${level.id}_beat`, 'true');
    }
  }, [level.targetMAE, level.id]);

  const { isRunning, clickCount, timeArray, handleClick, reset } = useGameEngine(modeConfig, handleGameOver);

  const handleRetry = () => { reset(); setResult(null); };

  const handleExit = () => {
    const returnTo = localStorage.getItem('arcadeReturn');
    localStorage.removeItem('arcadeReturn');
    navigate(returnTo || '/');
  };

  const liveMae = useMemo(() => {
    if (timeArray.length < 2) return null;
    const diffs = timeArray.slice(1).map((t, i) => t - timeArray[i]);
    const errs = diffs.map(d => Math.abs(d - 1000));
    return errs.reduce((s, e) => s + e, 0) / errs.length;
  }, [timeArray]);

  const nextLevel = ARCADE_LEVELS.find(l => l.levelNumber === level.levelNumber + 1) || null;

  const statusMsg = isRunning
    ? `Click ${level.timeHorizon - clickCount + 1} more time${level.timeHorizon - clickCount + 1 === 1 ? '' : 's'}…`
    : clickCount === 0
    ? theme.getIdleMsg(level)
    : 'Game over — see results';

  const { Background, LeftDecor, RightDecor, BottomBar } = theme;

  return (
    <div style={gameStyles.root}>
      <Background />
      <LeftDecor />
      <RightDecor />
      <BottomBar />

      {/* ── Main game panel ── */}
      <div style={{ ...gameStyles.panel, border: theme.panelBorder, background: theme.panelBg, marginBottom: theme.marginBottom }}>

        {/* Clipboard clip (doctor only) */}
        {theme.clipTop && (
          <div style={gameStyles.clipboardClip}>
            <div style={gameStyles.clipboardClipInner} />
          </div>
        )}

        {/* Receipt perforated top (rider only) */}
        {!theme.clipTop && (
          <div style={{ height: '10px', borderBottom: '3px dashed rgba(0,0,0,0.15)', margin: '0 8px', marginBottom: '0' }} />
        )}

        {/* Header */}
        <div style={{ ...gameStyles.panelHeader, background: theme.headerBg, borderBottom: theme.headerBorder }}>
          <span style={gameStyles.crossIcon}>{theme.headerIcon}</span>
          <div style={gameStyles.panelTitle}>
            <div style={gameStyles.panelTitleMain}>{theme.panelTitleMain}</div>
            <div style={gameStyles.panelTitleSub}>{theme.getPanelTitleSub(level)}</div>
          </div>
          <button style={gameStyles.exitBtn} onClick={handleExit}>✕</button>
        </div>

        {/* Challenge bar */}
        <div style={{ ...gameStyles.challengeBar, background: theme.challengeBarBg, borderBottom: theme.challengeBarBorder }}>
          <span style={{ ...gameStyles.challengeLabel, color: theme.challengeLabelColor }}>TARGET MAE:</span>
          <span style={{ ...gameStyles.challengeTarget, color: theme.challengeTargetColor }}>≤ {level.targetMAE} ms</span>
          {liveMae !== null && (
            <>
              <span style={{ ...gameStyles.challengeLabel, color: theme.challengeLabelColor }}>CURRENT:</span>
              <span style={{ ...gameStyles.challengeTarget, color: getMaeColor(liveMae, level.targetMAE) }}>
                {liveMae.toFixed(0)} ms
              </span>
            </>
          )}
        </div>

        {/* Progress circles */}
        <div style={gameStyles.circlesWrap}>
          <ProgressCircles
            clickCount={clickCount}
            timeArray={timeArray}
            selectedTimeHorizon={level.timeHorizon}
            isSoloMode={true}
          />
        </div>

        {/* Status message */}
        <div style={{ ...gameStyles.statusMsg, color: theme.statusColor, fontStyle: theme.statusFontStyle, fontFamily: theme.statusFontFamily }}>
          {statusMsg}
        </div>

        {/* Buttons */}
        <div style={gameStyles.btnArea}>
          <button
            style={{
              ...gameStyles.clickBtn,
              background: theme.clickBtnBg,
              border: theme.clickBtnBorder,
              boxShadow: theme.clickBtnShadow,
            }}
            onClick={handleClick}
          >
            {isRunning ? 'CLICK!' : 'START'}
          </button>
          <button style={{ ...gameStyles.retryBtn, border: theme.retryBtnBorder, color: theme.retryBtnColor }} onClick={handleRetry}>
            ↺ Reset
          </button>
        </div>
      </div>

      {result && (
        <ResultOverlay
          mae={result.mae}
          target={level.targetMAE}
          levelId={levelId}
          onRetry={handleRetry}
          onExit={handleExit}
          hasNext={!!nextLevel}
          onNext={() => nextLevel && navigate(`/arcade/${nextLevel.id}`)}
        />
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
        @keyframes clickPulse {
          0%   { box-shadow: ${theme.clickBtnShadow}; transform: scale(1); }
          50%  { box-shadow: ${theme.clickPulseKf};   transform: scale(1.025); }
          100% { box-shadow: ${theme.clickBtnShadow}; transform: scale(1); }
        }
        @keyframes lampSway {
          0%, 100% { transform: rotate(-3deg); }
          50%       { transform: rotate(3deg); }
        }
        @keyframes neonFlicker {
          0%, 92%, 96%, 100% { opacity: 1; }
          93%, 95%           { opacity: 0.6; }
          94%                { opacity: 0.85; }
        }
        @keyframes shinkansenRush {
          0%   { transform: translateX(calc(100vw + 800px)); }
          65%  { transform: translateX(-800px); }
          100% { transform: translateX(-800px); }
        }
        @keyframes concertPulse {
          0%, 100% { opacity: 0.7; }
          50%       { opacity: 1; }
        }
        @keyframes concertLED {
          0%   { background-position: 0px 0; }
          100% { background-position: 48px 0; }
        }
        @keyframes pendulumSwing {
          0%, 100% { transform: rotate(-8deg); }
          50%       { transform: rotate(8deg); }
        }
        .decor-hide { display: flex; }
        @media (max-width: 900px) { .decor-hide { display: none; } }
      `}</style>
    </div>
  );
};

const overlayStyles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(4px)' },
  card: { background: 'linear-gradient(160deg, #0d1b2a, #060e18)', border: '3px solid', padding: '36px 40px', textAlign: 'center', fontFamily: "'Press Start 2P', 'Courier New', monospace", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', minWidth: '320px', maxWidth: '400px', animation: 'fadeIn 0.3s ease' },
  resultTitle: { fontSize: '1.6rem', letterSpacing: '0.1em', textShadow: '0 0 24px currentColor' },
  resultSub: { color: 'rgba(200,220,255,0.7)', fontSize: '0.5rem', letterSpacing: '0.08em' },
  maeDisplay: { padding: '14px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: '100%', boxSizing: 'border-box' },
  maeLabel: { color: 'rgba(180,200,255,0.6)', fontSize: '0.45rem', letterSpacing: '0.1em' },
  maeVal: { fontSize: '1.4rem', fontWeight: 'bold', letterSpacing: '0.05em' },
  btnRow: { display: 'flex', gap: '12px', marginTop: '4px', flexWrap: 'wrap', justifyContent: 'center' },
  btn: { fontFamily: "'Press Start 2P', monospace", fontSize: '0.55rem', padding: '12px 18px', cursor: 'pointer', letterSpacing: '0.06em', border: '2px solid', transition: 'all 0.15s' },
  btnPrimary: { background: 'linear-gradient(135deg, #1565c0, #0d47a1)', borderColor: '#42a5f5', color: 'white', boxShadow: '0 4px 0 #0a2d6e' },
  btnSecondary: { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(220,230,255,0.8)' },
};

const gameStyles = {
  root: {
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Press Start 2P', 'Courier New', monospace",
  },
  panel: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    maxWidth: '480px',
    borderRadius: '4px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.8)',
    padding: '0 0 20px',
  },
  clipboardClip: {
    margin: '0 auto -6px',
    width: '70px', height: '20px',
    background: 'linear-gradient(180deg, #b0921c 0%, #8a7014 100%)',
    border: '2px solid #6a5010', borderRadius: '4px 4px 0 0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', zIndex: 1,
  },
  clipboardClipInner: {
    width: '30px', height: '10px',
    background: 'linear-gradient(180deg, #d4a820 0%, #a07818 100%)',
    borderRadius: '3px', border: '1px solid #7a5c10',
  },
  panelHeader: {
    padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  crossIcon: { color: 'white', fontSize: '1.4rem', textShadow: '0 0 8px rgba(255,255,255,0.4)', flexShrink: 0 },
  panelTitle: { flex: 1 },
  panelTitleMain: { color: 'white', fontSize: '0.65rem', letterSpacing: '0.12em', textShadow: '0 1px 3px rgba(0,0,0,0.4)', fontFamily: "'Press Start 2P', monospace" },
  panelTitleSub: { color: 'rgba(220,255,200,0.8)', fontSize: '0.42rem', marginTop: '3px', fontFamily: 'Georgia, serif', letterSpacing: '0.05em', fontStyle: 'italic' },
  exitBtn: { background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '50%', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  challengeBar: { padding: '8px 18px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  challengeLabel: { fontSize: '0.45rem', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.05em' },
  challengeTarget: { fontSize: '0.6rem', fontFamily: "'Press Start 2P', monospace", fontWeight: 'bold', marginRight: '8px' },
  circlesWrap: { padding: '14px 16px 6px', borderBottom: '1px solid rgba(0,0,0,0.08)' },
  statusMsg: { padding: '10px 18px', fontSize: '0.42rem', textAlign: 'center', letterSpacing: '0.04em', borderBottom: '1px solid rgba(0,0,0,0.08)', minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  btnArea: { padding: '16px 18px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
  clickBtn: { width: '100%', padding: '16px', color: 'white', fontFamily: "'Press Start 2P', monospace", fontSize: '0.85rem', letterSpacing: '0.15em', cursor: 'pointer', transition: 'transform 0.08s, box-shadow 0.08s', textShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  retryBtn: { background: 'none', fontFamily: "'Press Start 2P', monospace", fontSize: '0.45rem', padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.06em' },
};

export default ArcadeGame;
